import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Container,
  Paper,
  Alert,
  Chip,
  Dialog,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Snackbar,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Upload as UploadIcon,
  TextFields as TextIcon,
  ShoppingCart as CartIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

import { generatePanelSKUWithTimestamp, generateSKUWithTimestamp } from '../services/skuService';
import clientImageCaptureService from '../services/clientImageCaptureService';
import { calculatePanelPrice } from '../services/panelPricingService';
import { API_ENDPOINTS } from '../config';
import { buildConnectorFamilies } from '../services/connectorFamiliesService';
import { compatibilityMatrix } from '../services/compatibilityMatrix';
import { panelBigCommerceService } from '../services/panelBigCommerceService';
import { findNextAvailablePosition as findNextAvailablePositionUtil, validateWireTailings as validateWireTailingsUtil } from './panelBuilderUtils';
import SimplifiedFinishingSelection from './SimplifiedFinishingSelection';
import PanelReviewCart from './PanelReviewCart';
import {
  getCutoutGridSpanForFootprint,
  getGridSpanForFootprint,
  getPanelConnectorFootprint,
  getPlacementGridSpanForFootprint
} from '../services/panelConnectorFootprints';
import {
  closeCartTab,
  navigateCartTab,
  openCartTabDuringGesture
} from '../utils/connectorDisplayUtils';

// Panel Builder Main Component
function PanelBuilder({ onAddToCart, cart = [] }) {
  const isDev = process.env.NODE_ENV === 'development';

  const nlfxColors = {
    primary: '#1a1a1a',
    secondary: '#b32b2b',
    text: '#ffffff',
    background: '#f5f5f5',
    card: '#ffffff'
  };

  // Set page title for PanelBuilder route only
  useEffect(() => {
    const previousTitle = document.title;
    const descriptionEl = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionEl?.getAttribute('content') || '';

    document.title = 'NLFX Professional Custom Panel Builder';
    if (descriptionEl) {
      descriptionEl.setAttribute(
        'content',
        'NLFX Professional Custom Panel Builder - Design your perfect panel'
      );
    }
    return () => {
      document.title = previousTitle;
      if (descriptionEl) {
        descriptionEl.setAttribute('content', previousDescription);
      }
    };
  }, []);

  // Panel configuration state
  const [panelConfig, setPanelConfig] = useState({
    size: { width: 19, height: 1.75 }, // Standard 1U rack panel dimensions (inches)
    material: 'flanged-aluminum', // Panel material
    gridSize: 0.125, // 1/8" grid resolution for precise connector placement
    connectorSize: { width: 1.125, height: 1.25 }, // Full 9x10 grid cells (9 * 0.125 = 1.125", 10 * 0.125 = 1.25")
    connectors: [], // Array of placed connectors
    customLogo: null, // Will be { url: string, position: { x: number, y: number } }
    customText: [],
    wireTailings: [], // Wire configurations for each connector
    totalPrice: 0,
    sku: null
  });

  // UI state
  const [activeStep, setActiveStep] = useState(0);
  const [showTextDialog, setShowTextDialog] = useState(false);
  const [connectorFamilies, setConnectorFamilies] = useState([]);
  const [selectedConnectorType, setSelectedConnectorType] = useState(null);
  const [selectedConnectorFamily, setSelectedConnectorFamily] = useState(null);
  const [draggedConnector, setDraggedConnector] = useState(null);
  const [currentDragPosition, setCurrentDragPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [draggedText, setDraggedText] = useState(null);
  const [draggedLogo, setDraggedLogo] = useState(null);
  const [showConnectorPreview, setShowConnectorPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [customTextInput, setCustomTextInput] = useState('');
  const textFormatting = { maxCharacters: 12 };
  const [customTexts, setCustomTexts] = useState([]);
  const [autoLabelConnectors, setAutoLabelConnectors] = useState(true);
  // Used for finishing uploads (custom heat shrink logo) before checkout assigns a final SKU.
  const [designSku] = useState(() => panelConfig.sku || generateSKUWithTimestamp('PANEL-DRAFT'));

  const finishingUsesCustomHeatShrink = useCallback((finishing) => {
    const heat = finishing?.heatShrink;
    if (!heat || typeof heat !== 'object') return false;
    return Object.entries(heat).some(
      ([k, v]) => k !== 'global' && (v === 'custom' || v === 'clear+custom')
    );
  }, []);

  const compressImageFileToDataUrl = useCallback(async (file, opts = {}) => {
    const {
      maxDim = 700,
      targetBytes = 180_000,
      minQuality = 0.5,
      startQuality = 0.82
    } = opts;

    const srcDataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read image file'));
      reader.readAsDataURL(file);
    });
    if (typeof srcDataUrl !== 'string') return null;

    const img = new Image();
    const loaded = await new Promise((resolve, reject) => {
      img.onload = () => resolve(true);
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.src = srcDataUrl;
    });
    if (!loaded) return null;

    const scale = Math.min(1, maxDim / Math.max(img.width || 1, img.height || 1));
    const w = Math.max(1, Math.round((img.width || 1) * scale));
    const h = Math.max(1, Math.round((img.height || 1) * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);

    const approxBytes = (dataUrl) => {
      if (typeof dataUrl !== 'string') return Infinity;
      const idx = dataUrl.indexOf(',');
      if (idx < 0) return Infinity;
      const base64 = dataUrl.slice(idx + 1);
      return Math.floor((base64.length * 3) / 4);
    };

    let quality = startQuality;
    let out = canvas.toDataURL('image/jpeg', quality);
    let bytes = approxBytes(out);
    while (bytes > targetBytes && quality > minQuality) {
      quality = Math.max(minQuality, quality - 0.08);
      out = canvas.toDataURL('image/jpeg', quality);
      bytes = approxBytes(out);
    }

    return out;
  }, []);

  // History API: make browser back go to previous panel-builder step instead of leaving `/panel-builder`.
  const isPanelPopStateRef = useRef(false);
  const isPanelFirstStepEffectRef = useRef(true);
  const PANEL_HISTORY_STATE_KEY = 'nlfx_panel_builder_step';

  useEffect(() => {
    if (typeof window === 'undefined' || !window.history) return;
    const state = { [PANEL_HISTORY_STATE_KEY]: 0 };
    if (!window.history.state || window.history.state[PANEL_HISTORY_STATE_KEY] === undefined) {
      window.history.replaceState(state, '', window.location.href);
    }
    const onPopState = (event) => {
      const step =
        event.state && typeof event.state[PANEL_HISTORY_STATE_KEY] === 'number'
          ? event.state[PANEL_HISTORY_STATE_KEY]
          : null;
      if (step === null) return;
      isPanelPopStateRef.current = true;
      setActiveStep(step);
      setTimeout(() => {
        isPanelPopStateRef.current = false;
      }, 0);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.history || isPanelPopStateRef.current) return;
    if (isPanelFirstStepEffectRef.current) {
      isPanelFirstStepEffectRef.current = false;
      return;
    }
    window.history.pushState({ [PANEL_HISTORY_STATE_KEY]: activeStep }, '', window.location.href);
  }, [activeStep]);

  // Wire tailing validation state
  const [wireTailingValidation, setWireTailingValidation] = useState({
    isValid: false,
    missingRequirements: []
  });

  // Advanced Grid Scaling State
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

  // Grid and panel dimensions
  const gridCols = Math.floor(panelConfig.size.width / panelConfig.gridSize);
  const gridRows = Math.floor(panelConfig.size.height / panelConfig.gridSize);
  
  // Connector size in grid units
  const connectorWidthGrid = Math.ceil(panelConfig.connectorSize.width / panelConfig.gridSize);
  const connectorHeightGrid = Math.ceil(panelConfig.connectorSize.height / panelConfig.gridSize);

  const getEffectiveLogoCells = useCallback(
    (logo) => {
      if (!logo) return { widthCells: 0, heightCells: 0, multiplier: 1 };
      const mult = Number.isFinite(Number(logo.sizeMultiplier)) ? Number(logo.sizeMultiplier) : 1;
      const baseW = Number.isFinite(Number(logo.width)) ? Number(logo.width) : 6;
      const baseH = Number.isFinite(Number(logo.height)) ? Number(logo.height) : 3;

      const unclampedW = Math.max(1, Math.round(baseW * mult));
      const unclampedH = Math.max(1, Math.round(baseH * mult));

      // Hard cap to the actual grid so the logo can never exceed panel bounds.
      const widthCells = Math.max(1, Math.min(gridCols, unclampedW));
      const heightCells = Math.max(1, Math.min(gridRows, unclampedH));

      return { widthCells, heightCells, multiplier: mult };
    },
    [gridCols, gridRows]
  );

  const getConnectorSpanCells = useCallback((connectorLike) => {
    const footprint = getPanelConnectorFootprint({
      family: connectorLike?.family,
      type: connectorLike?.type
    });
    const placement = getPlacementGridSpanForFootprint({
      footprint,
      gridSizeInches: panelConfig.gridSize
    });
    const cutout = getCutoutGridSpanForFootprint({
      footprint,
      gridSizeInches: panelConfig.gridSize
    });

    return {
      // Placement envelope (keepout) used for snapping/collisions
      width: placement.widthCells,
      height: placement.heightCells,
      // Visual cutout envelope used for images + label anchoring
      cutoutWidth: cutout.widthCells,
      cutoutHeight: cutout.heightCells,
      footprint
    };
  }, [panelConfig.gridSize]);

  const alignPlacedConnectors = useCallback(
    (mode) => {
      const placed = Array.isArray(panelConfig.connectors) ? panelConfig.connectors : [];
      if (placed.length === 0) return;

      const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

      // Compute current cutout top/bottom/center in grid-cell coordinates (may be fractional).
      const metrics = placed.map((c) => {
        const span = getConnectorSpanCells(c);
        const placementH = span?.height ?? 10;
        const cutH = span?.cutoutHeight ?? placementH;
        const cutOffsetTop = (placementH - cutH) / 2; // centered cutout within placement box
        const cutTop = (c.position?.row ?? 0) + cutOffsetTop;
        const cutBottom = cutTop + cutH;
        const cutCenter = cutTop + cutH / 2;
        return { id: c.id, cutTop, cutBottom, cutCenter, cutOffsetTop, placementH };
      });

      // Lane logic (1L/2L/3L/4L): if connectors are already in multiple vertical "rows",
      // align within each lane independently instead of forcing everything into one line.
      const sortedByCenter = [...metrics].sort((a, b) => a.cutCenter - b.cutCenter);
      const gapThreshold = 2.5; // cells; separates lanes for 2L/3L/4L behavior
      const lanes = [];
      sortedByCenter.forEach((m) => {
        const last = lanes[lanes.length - 1];
        if (!last) {
          lanes.push([m]);
          return;
        }
        const lastAvg = last.reduce((s, x) => s + x.cutCenter, 0) / last.length;
        if (Math.abs(m.cutCenter - lastAvg) > gapThreshold) lanes.push([m]);
        else last.push(m);
      });
      // Cap to 4 lanes (merge extras into the closest previous lane).
      while (lanes.length > 4) {
        const extra = lanes.pop();
        lanes[lanes.length - 1].push(...extra);
      }

      // Assign each connector to a lane exactly once (prevents "double click" behavior).
      const laneIndexById = new Map();
      lanes.forEach((lane, idx) => lane.forEach((m) => laneIndexById.set(m.id, idx)));

      // Align lanes to grid "bands" so Top/Middle/Bottom correspond to the grid, not just relative alignment.
      const L = Math.max(1, lanes.length);
      const bandHeight = gridRows / L;
      const band = (laneIdx) => {
        const top = laneIdx * bandHeight;
        const bottom = (laneIdx + 1) * bandHeight;
        return { top, bottom, middle: (top + bottom) / 2 };
      };

      setPanelConfig((prev) => {
        const next = (Array.isArray(prev.connectors) ? prev.connectors : []).map((c) => {
          const m = metrics.find((x) => x.id === c.id);
          if (!m) return c;

          let nextRow = c.position?.row ?? 0;
          const laneIdx = laneIndexById.get(c.id) ?? 0;
          const b = band(laneIdx);

          if (mode === 'top') {
            nextRow = Math.round(b.top - m.cutOffsetTop);
          } else if (mode === 'bottom') {
            const cutH = (m.cutBottom - m.cutTop);
            nextRow = Math.round(b.bottom - cutH - m.cutOffsetTop);
          } else if (mode === 'middle') {
            const cutH = (m.cutBottom - m.cutTop);
            nextRow = Math.round(b.middle - cutH / 2 - m.cutOffsetTop);
          } else {
            return c;
          }

          const maxRow = Math.max(0, gridRows - (m.placementH ?? 10));
          nextRow = clamp(nextRow, 0, maxRow);
          return { ...c, position: { ...(c.position || {}), row: nextRow } };
        });

        return { ...prev, connectors: next };
      });
    },
    [panelConfig.connectors, getConnectorSpanCells, gridRows]
  );

  const selectedPlacementSpan = useMemo(() => {
    if (!selectedConnectorType) return null;
    return getConnectorSpanCells({
      family: selectedConnectorType.family || selectedConnectorType.name,
      type: selectedConnectorType.type || selectedConnectorType.name
    });
  }, [getConnectorSpanCells, selectedConnectorType]);

  const AUTO_LABEL_TEXT_ROWS = 3;
  const MIN_LABEL_COLS = 8;
  const MAX_LABEL_COLS = 28;

  const estimateLabelColsForText = useCallback((rawText) => {
    const len = String(rawText || '').length;
    if (!len) return MIN_LABEL_COLS;
    // Rough heuristic: short labels don't need huge boxes; long family names need more grid width.
    const cols = Math.ceil(len * 0.65) + 4;
    return Math.max(MIN_LABEL_COLS, Math.min(MAX_LABEL_COLS, cols));
  }, []);

  const estimateLabelRowsForText = useCallback(
    (rawText) => {
      const len = String(rawText || '').length;
      const cols = estimateLabelColsForText(rawText);
      const approxCharsPerRow = Math.max(10, Math.floor(cols * 1.35));
      const rowsNeeded = Math.ceil(len / approxCharsPerRow);
      return Math.min(6, Math.max(AUTO_LABEL_TEXT_ROWS, rowsNeeded));
    },
    [estimateLabelColsForText]
  );

  const isConnectorReferenceLabel = useCallback((t) => {
    if (!t) return false;
    if (t.linkedConnectorId) return true;
    // Back-compat: older auto labels might not have linkedConnectorId set.
    if (typeof t.id === 'string' && t.id.startsWith('label_')) return true;
    const content = String(t.content || '');
    return /^\s*#\d+\s*\|\s*/.test(content);
  }, []);

  /** Connector reference labels hide when “Show connector labels” is off; data stays in panelConfig.customText. */
  const visibleCustomTextItems = useMemo(
    () =>
      panelConfig.customText.filter(
        (t) => !isConnectorReferenceLabel(t) || autoLabelConnectors
      ),
    [panelConfig.customText, autoLabelConnectors, isConnectorReferenceLabel]
  );

  /** Engraved “Custom Text” only — excludes connector reference labels (see panel grid section). */
  const engravedCustomTextItems = useMemo(
    () => panelConfig.customText.filter((t) => !isConnectorReferenceLabel(t)),
    [panelConfig.customText, isConnectorReferenceLabel]
  );

  const connectorReferenceLabelItems = useMemo(
    () => panelConfig.customText.filter((t) => isConnectorReferenceLabel(t)),
    [panelConfig.customText, isConnectorReferenceLabel]
  );

  const formatAutoConnectorLabelText = useCallback((indexOneBased, connectorLike) => {
    const idx = Number(indexOneBased) || 0;
    const family = String(connectorLike?.family || '').trim();
    const type = String(connectorLike?.type || '').trim();

    // Prefer family as the primary "connector type" label; fall back to type if family is missing.
    // Important: spacing + delimiter avoids "12 1/4\"" looking like "1¼" / measurement confusion.
    const humanType = family || type || 'Connector';
    return `#${idx}\u2009|\u2009${humanType}`;
  }, []);

  const handleConnectorFamilyChipClick = useCallback((family) => {
    // Selecting a connector family should always cancel the active placement tool from the previous family.
    setSelectedConnectorType(null);
    setShowConnectorPreview(false);

    setSelectedConnectorFamily((prev) => (prev?.id === family.id ? null : family));
  }, []);

  const applyConnectorGridMove = useCallback((prev, connectorId, nextRow, nextCol) => {
    const nextConnectors = prev.connectors.map((conn) =>
      conn.id === connectorId ? { ...conn, position: { row: nextRow, col: nextCol } } : conn
    );

    // Linked labels are rendered relative to the connector + linkedOffset, so we don't need to
    // mutate stored text positions on connector moves (doing so can double-apply movement).

    return { ...prev, connectors: nextConnectors };
  }, []);


  // Responsive scaling calculations
  const calculateOptimalCellSize = useCallback(() => {
    if (viewportSize.width === 0 || viewportSize.height === 0) return 12;
    
    const availableWidth = Math.min(viewportSize.width * 0.95, 1200);
    const availableHeight = Math.min(viewportSize.height * 0.7, 800);
    
    const cellSizeByWidth = availableWidth / gridCols;
    const cellSizeByHeight = availableHeight / gridRows;
    
    const optimalSize = Math.min(cellSizeByWidth, cellSizeByHeight);
    return Math.max(8, Math.min(optimalSize, 24)); // Min 8px, Max 24px
  }, [viewportSize, gridCols, gridRows]);

  const currentCellSize = calculateOptimalCellSize();

  // Viewport tracking and responsive updates
  useEffect(() => {
    const updateViewportSize = () => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };

    updateViewportSize();
    window.addEventListener('resize', updateViewportSize);
    window.addEventListener('orientationchange', updateViewportSize);

    return () => {
      window.removeEventListener('resize', updateViewportSize);
      window.removeEventListener('orientationchange', updateViewportSize);
    };
  }, []);

  // Zoom and pan controls



  // Panel size presets - Flanged Aluminum and Black Anodized
  const panelSizePresets = [
    // Flanged Aluminum Rack Panels
    { name: '1U Flanged Aluminum', width: 19, height: 1.75, material: 'flanged-aluminum', description: 'Standard 1U rack mount - Flanged aluminum' },
    { name: '2U Flanged Aluminum', width: 19, height: 3.5, material: 'flanged-aluminum', description: 'Standard 2U rack mount - Flanged aluminum' },
    { name: '3U Flanged Aluminum', width: 19, height: 5.25, material: 'flanged-aluminum', description: 'Standard 3U rack mount - Flanged aluminum' },
    { name: '4U Flanged Aluminum', width: 19, height: 7, material: 'flanged-aluminum', description: 'Standard 4U rack mount - Flanged aluminum' },
    
    // Black Anodized Gang Panels
    { name: '1G Black Anodized', width: 4.5, height: 4.5, material: 'black-anodized', description: '1-gang electrical box - Black anodized aluminum' },
    { name: '2G Black Anodized', width: 4.5, height: 4.5, material: 'black-anodized', description: '2-gang electrical box - Black anodized aluminum' },
    { name: '3G Black Anodized', width: 6.75, height: 4.5, material: 'black-anodized', description: '3-gang electrical box - Black anodized aluminum' },
    { name: '4G Black Anodized', width: 9, height: 4.5, material: 'black-anodized', description: '4-gang electrical box - Black anodized aluminum' }
  ];

  // Steps for the panel builder
  const steps = [
    'Panel Size',
    'Connectors & Customization',
    'Review & Price'
  ];

  // Calculate panel price when configuration changes
  // Load connector families on component mount
  useEffect(() => {
    const loadConnectorFamilies = async () => {
      try {
        // Get all connector families for panel mount connectors
        // Include DMX so 5-pin XLR panel variants show up under “Choose specific connector”.
        const audioFamilies = buildConnectorFamilies('audio', { requireHybrid: false });
        const dmxFamilies = buildConnectorFamilies('dmx', { requireHybrid: false });
        const powerFamilies = buildConnectorFamilies('power', { requireHybrid: false });

        const mergeFamilies = (families) => {
          const byId = new Map();
          (families || []).forEach((fam) => {
            if (!fam) return;
            const key = fam.id || fam.name;
            if (!key) return;
            const existing = byId.get(key);
            if (!existing) {
              byId.set(key, { ...fam, variants: Array.isArray(fam.variants) ? [...fam.variants] : [] });
              return;
            }
            const existingVariants = Array.isArray(existing.variants) ? existing.variants : [];
            const nextVariants = Array.isArray(fam.variants) ? fam.variants : [];
            const merged = [...existingVariants];
            nextVariants.forEach((v) => {
              if (!v?.id) return;
              if (merged.some((m) => m.id === v.id)) return;
              merged.push(v);
            });
            byId.set(key, { ...existing, ...fam, variants: merged, image: existing.image || fam.image });
          });
          return Array.from(byId.values());
        };

        const allFamilies = mergeFamilies([...audioFamilies, ...dmxFamilies, ...powerFamilies]);
        
        // Filter for panel mount connectors only
        const panelMountFamilies = allFamilies.filter(family => 
          family.variants.some(variant => variant.mountType === 'panel')
        );
        
        if (isDev) {
          console.log('🔍 Loaded connector families:', panelMountFamilies);
          console.log('🔍 First family variants:', panelMountFamilies[0]?.variants);
        }
        setConnectorFamilies(panelMountFamilies);
      } catch (error) {
        console.error('Error loading connector families:', error);
      }
    };
    
    loadConnectorFamilies();
  }, []);

  // Handle mouse move and touch move for drag overlay
  useEffect(() => {
    let animationFrameId = null;
    
    const handleMouseMove = (event) => {
      if (isDragging && (draggedConnector || draggedText || draggedLogo)) {
        // Throttle updates using requestAnimationFrame
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        animationFrameId = requestAnimationFrame(() => {
          setCurrentDragPosition({ x: event.clientX, y: event.clientY });
        });
      }
    };

    const handleMouseUp = (event) => {
      if (isDragging && (draggedConnector || draggedText || draggedLogo)) {
        if (isDev) console.log('Global mouse up detected, triggering drag end');
        if (draggedConnector) {
          handleDragEnd(event);
        } else if (draggedText) {
          // Handle text drag end with proper drop logic
          handleTextDragEnd(event);
        } else if (draggedLogo) {
          // Handle logo drag end with proper drop logic
          handleLogoDragEnd(event);
        }
      }
    };

    const handleTouchMove = (event) => {
      if (isDragging && (draggedConnector || draggedText || draggedLogo)) {
        event.preventDefault();
        const touch = event.touches[0];
        setCurrentDragPosition({ x: touch.clientX, y: touch.clientY });
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      return () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
      };
    }
  }, [isDragging, draggedConnector, draggedText, draggedLogo]);

  // Debounced pricing calculation to prevent performance issues
  const updatePricing = useCallback(async () => {
    try {
      const price = await calculatePanelPrice(panelConfig);
      setPanelConfig(prev => ({ ...prev, totalPrice: price }));
    } catch (error) {
      console.error('Error calculating panel price:', error);
    }
  }, [panelConfig.size, panelConfig.material, panelConfig.connectors, panelConfig.wireTailings, panelConfig.customText, panelConfig.customLogo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced pricing update to prevent excessive calculations
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updatePricing();
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [updatePricing]);

  // Handle panel size change
  const handlePanelSizeChange = (preset) => {
    setPanelConfig(prev => ({
      ...prev,
      size: { width: preset.width, height: preset.height },
      material: preset.material
    }));
    // Pricing will be updated automatically via useEffect
  };

  // Function to find next available position for connector placement
  const findNextAvailablePosition = (preferredCol, preferredRow, movingConnectorLike) => {
    const moving = getConnectorSpanCells(movingConnectorLike);
    return findNextAvailablePositionUtil({
      preferredCol,
      preferredRow,
      gridCols,
      gridRows,
      connectors: panelConfig.connectors,
      connectorWidth: moving.width,
      connectorHeight: moving.height,
      getConnectorSpan: (placed) => getConnectorSpanCells(placed)
    });
  };

  // Handle connector placement on grid
  const handleGridClick = (event) => {
    if (selectedConnectorType) {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      // Calculate grid position based on click coordinates
      const cellWidth = rect.width / gridCols;
      const cellHeight = rect.height / gridRows;
      
      // Convert mouse position to grid coordinates
      const mouseCol = x / cellWidth;
      const mouseRow = y / cellHeight;
      
      // Connector dimensions in grid cells
      const moving = getConnectorSpanCells({
        family: selectedConnectorType.family || selectedConnectorType.name,
        type: selectedConnectorType.type || selectedConnectorType.name
      });
      const connectorWidth = moving.width;
      const connectorHeight = moving.height;
      
      // Center the connector on the click point
      const centeredMouseCol = mouseCol - connectorWidth / 2;
      const centeredMouseRow = mouseRow - connectorHeight / 2;
      
      // Snap to grid boundaries and ensure connector stays within grid
      const preferredCol = Math.max(0, Math.min(gridCols - connectorWidth, Math.round(centeredMouseCol)));
      const preferredRow = Math.max(0, Math.min(gridRows - connectorHeight, Math.round(centeredMouseRow)));
      
      // Find the next available position (will shift if there's overlap)
      const { col: finalCol, row: finalRow } = findNextAvailablePosition(preferredCol, preferredRow, {
        family: selectedConnectorType.family || selectedConnectorType.name,
        type: selectedConnectorType.type || selectedConnectorType.name
      });
      
      // Always place the connector - findNextAvailablePosition handles collision detection
        // Check if we have a specific connector selected (from the variant selection)
        if (selectedConnectorType.connectorId) {
          // This is a specific connector variant
          const newConnector = {
            id: `connector_${Date.now()}`,
            type: selectedConnectorType.type,
            connectorId: selectedConnectorType.connectorId,
            position: { row: finalRow, col: finalCol },
            image: selectedConnectorType.image,
            family: selectedConnectorType.family,
            description: selectedConnectorType.description,
            gender: selectedConnectorType.gender,
            poles: selectedConnectorType.poles,
            wireTailing: null
          };
          
          setPanelConfig(prev => {
            const nextConnectors = [...prev.connectors, newConnector];
            const span = getConnectorSpanCells(newConnector);
            const labelContent = formatAutoConnectorLabelText(nextConnectors.length, newConnector);
            const labelCols = estimateLabelColsForText(labelContent);
            const labelRows = estimateLabelRowsForText(labelContent);
            const labelRow = Math.max(
              0,
              Math.min(
                gridRows - labelRows,
                newConnector.position.row - (labelRows + 1)
              )
            );
            const labelCol = Math.max(
              0,
              Math.min(
                gridCols - labelCols,
                Math.round(newConnector.position.col + span.cutoutWidth / 2 - labelCols / 2)
              )
            );
            const nextCustomText = autoLabelConnectors ? [
              ...prev.customText,
              {
                id: `label_${newConnector.id}`,
                content: labelContent,
                linkedConnectorId: newConnector.id,
                linkedOffset: { dRow: -(labelRows + 1), dCol: 0 },
                position: {
                  row: labelRow,
                  col: labelCol
                }
              }
            ] : prev.customText;

            return {
              ...prev,
              connectors: nextConnectors,
              customText: nextCustomText
            };
          });
          
          // Keep the specific connector selected for multiple placements
          // Don't reset the selection - let user manually deselect
        } else {
          // This is a family selection, get the first panel mount variant
          const panelMountVariant = selectedConnectorType.variants.find(variant => 
            variant.mountType === 'panel'
          );
          
          if (panelMountVariant) {
            const newConnector = {
              id: `connector_${Date.now()}`,
              type: selectedConnectorType.name,
              connectorId: panelMountVariant.id,
              position: { row: finalRow, col: finalCol },
              image: panelMountVariant.image,
              family: selectedConnectorType.name,
              description: panelMountVariant.description || panelMountVariant.name,
              gender: panelMountVariant.gender,
              poles: panelMountVariant.poles,
              wireTailing: null
            };
            
            setPanelConfig(prev => {
              const nextConnectors = [...prev.connectors, newConnector];
              const span = getConnectorSpanCells(newConnector);
              const labelContent = formatAutoConnectorLabelText(nextConnectors.length, newConnector);
              const labelCols = estimateLabelColsForText(labelContent);
              const labelRows = estimateLabelRowsForText(labelContent);
              const labelRow = Math.max(
                0,
                Math.min(
                  gridRows - labelRows,
                  newConnector.position.row - (labelRows + 1)
                )
              );
              const labelCol = Math.max(
                0,
                Math.min(
                  gridCols - labelCols,
                  Math.round(newConnector.position.col + span.cutoutWidth / 2 - labelCols / 2)
                )
              );
              const nextCustomText = autoLabelConnectors ? [
                ...prev.customText,
                {
                  id: `label_${newConnector.id}`,
                  content: labelContent,
                  linkedConnectorId: newConnector.id,
                  linkedOffset: { dRow: -(labelRows + 1), dCol: 0 },
                  position: {
                    row: labelRow,
                    col: labelCol
                  }
                }
              ] : prev.customText;

              return {
                ...prev,
                connectors: nextConnectors,
                customText: nextCustomText
              };
            });
            
            // Keep the family selected for multiple placements
            // Only reset if it's a family selection, not a specific connector
          }
        }
      }
  };

  // Handle drag start for connectors
  const handleDragStart = (connector, event) => {
    event.preventDefault();
    console.log('Drag start for connector:', connector.id);
    
    // Calculate offset from mouse to connector center (not bottom-right)
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - (rect.left + rect.width / 2); // Center horizontally
    const offsetY = event.clientY - (rect.top + rect.height / 2); // Center vertically
    
    setDraggedConnector(connector);
    setDragStartPosition({ x: event.clientX, y: event.clientY });
    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);
    
    // Set initial drag position
    setCurrentDragPosition({ x: event.clientX, y: event.clientY });
    
    console.log('Drag state set:', { 
      draggedConnector: connector.id, 
      isDragging: true, 
      position: { x: event.clientX, y: event.clientY } 
    });
  };

  // Handle drag over - calculate mouse position and convert to grid coordinates
  const handleDragOver = (event) => {
    if (draggedConnector && isDragging) {
      event.preventDefault();
      // Keep screen coordinates so the floating drag overlay (renderDragOverlay) tracks the cursor.
      // Grid snap happens on drop in handleDragEnd — do not overwrite {x,y} with {row,col}.
      setCurrentDragPosition({ x: event.clientX, y: event.clientY });
    }
  };

  // Handle drag end for connectors
  const handleDragEnd = (event) => {
    if (draggedConnector && isDragging) {
      event.preventDefault();
      event.stopPropagation();
      console.log('Drag end triggered for connector:', draggedConnector.id);
      
      // Get the grid container
      const gridContainer = document.querySelector('[data-grid-container]');
      
      if (gridContainer) {
        const rect = gridContainer.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        // Check if mouse is still within grid bounds
        const isWithinGrid = x >= 0 && x <= rect.width && y >= 0 && y <= rect.height;
        
        if (isWithinGrid) {
          console.log('Drop coordinates:', { x, y, rect: { width: rect.width, height: rect.height } });
          
          // Calculate grid position based on mouse coordinates
          const cellWidth = rect.width / gridCols;
          const cellHeight = rect.height / gridRows;
          
          // Snap connector to grid boundaries - align left edge to grid cell boundaries
          const mouseCol = x / cellWidth;
          const mouseRow = y / cellHeight;
          // Center connector on drop position, then align with grid boundaries
          const moving = getConnectorSpanCells(draggedConnector);
          const dragConnWidth = moving.width;
          const dragConnHeight = moving.height;
          
          // Center the connector on the drop position
          const centeredMouseCol = mouseCol - dragConnWidth / 2;
          const centeredMouseRow = mouseRow - dragConnHeight / 2;
          
          const preferredCol = Math.max(0, Math.min(gridCols - dragConnWidth, Math.round(centeredMouseCol)));
          const preferredRow = Math.max(0, Math.min(gridRows - dragConnHeight, Math.round(centeredMouseRow)));
          
          // Find the next available position (will shift if there's overlap)
          const { col: centeredCol, row: centeredRow } = findNextAvailablePosition(preferredCol, preferredRow, draggedConnector);
          
          console.log('Calculated grid position:', { mouseRow, mouseCol, preferredRow, preferredCol, centeredRow, centeredCol });
          
          // Always move the connector to the calculated position
          // findNextAvailablePosition already handles collision detection and finds the closest available position
          console.log('Moving connector to new position:', { centeredRow, centeredCol });
          setPanelConfig((prev) => applyConnectorGridMove(prev, draggedConnector.id, centeredRow, centeredCol));
        } else {
          console.log('Mouse outside grid, connector returns to original position');
        }
      } else {
        console.log('Grid container not found');
      }
      
      console.log('🎯 Drag end completed for connector:', draggedConnector.id);
      setDraggedConnector(null);
      setCurrentDragPosition(null);
      setIsDragging(false);
    }
  };

  // Handle touch events for mobile drag and drop
  const handleConnectorTouchStart = (connector, event) => {
    event.preventDefault();
    console.log('Touch start for connector:', connector.id);
    
    // Get the touch coordinates
    const touch = event.touches[0];
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = touch.clientX - (rect.left + rect.width / 2); // Center horizontally
    const offsetY = touch.clientY - (rect.top + rect.height / 2); // Center vertically
    
    setDraggedConnector(connector);
    setDragStartPosition({ x: touch.clientX, y: touch.clientY });
    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);
    setCurrentDragPosition({ x: touch.clientX, y: touch.clientY });
  };

  const handleConnectorTouchMove = (event) => {
    if (draggedConnector && isDragging) {
      event.preventDefault();
      const touch = event.touches[0];
      setCurrentDragPosition({ x: touch.clientX, y: touch.clientY });
    }
  };

  const handleConnectorTouchEnd = (event) => {
    if (draggedConnector && isDragging) {
      event.preventDefault();
      console.log('Touch end for connector:', draggedConnector.id);
      
      // Get the grid container
      const gridContainer = document.querySelector('[data-grid-container]');
      
      if (gridContainer) {
        const touch = event.changedTouches[0];
        const rect = gridContainer.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        
        // Check if touch is still within grid bounds
        const isWithinGrid = x >= 0 && x <= rect.width && y >= 0 && y <= rect.height;
        
        if (isWithinGrid) {
          console.log('Touch drop coordinates:', { x, y, rect: { width: rect.width, height: rect.height } });
          
          // Calculate grid position based on touch coordinates
          const cellWidth = rect.width / gridCols;
          const cellHeight = rect.height / gridRows;
          
          // Center the connector on the touch position
          const touchCol = x / cellWidth;
          const touchRow = y / cellHeight;
          // Center connector on touch position, then align with grid boundaries
          const moving = getConnectorSpanCells(draggedConnector);
          const touchConnWidth = moving.width;
          const touchConnHeight = moving.height;
          
          // Center the connector on the touch position
          const centeredTouchCol = touchCol - touchConnWidth / 2;
          const centeredTouchRow = touchRow - touchConnHeight / 2;
          
          const centeredCol = Math.max(0, Math.min(gridCols - touchConnWidth, Math.round(centeredTouchCol)));
          const centeredRow = Math.max(0, Math.min(gridRows - touchConnHeight, Math.round(centeredTouchRow)));
          
          console.log('Calculated grid position:', { touchRow, touchCol, centeredRow, centeredCol });
          
          // Always move the connector to the calculated position
          // The collision detection is handled by the boundary constraints
          console.log('Moving connector to new position:', { centeredRow, centeredCol });
          setPanelConfig((prev) => applyConnectorGridMove(prev, draggedConnector.id, centeredRow, centeredCol));
        } else {
          console.log('Touch outside grid, connector returns to original position');
        }
      } else {
        console.log('Grid container not found');
      }
      
      console.log('🎯 Touch end completed for connector:', draggedConnector.id);
      setDraggedConnector(null);
      setCurrentDragPosition(null);
      setIsDragging(false);
    }
  };

  // Handle text drag end
  const handleTextDragEnd = (event) => {
    if (draggedText && isDragging) {
      event.preventDefault();
      console.log('Text drag end for:', draggedText.id);
      
      // Get the grid container
      const gridContainer = document.querySelector('[data-grid-container]');
      if (gridContainer) {
        const rect = gridContainer.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        // Check if drop is within grid bounds
        const isWithinGrid = x >= 0 && x <= rect.width && y >= 0 && y <= rect.height;
        
        if (isWithinGrid) {
          // Calculate grid position based on drop coordinates
          const cellWidth = rect.width / gridCols;
          const cellHeight = rect.height / gridRows;
          
          // Center text on drop position
          const textWidth = estimateLabelColsForText(draggedText?.content); // Dynamic width so labels don't truncate
          const textHeight = estimateLabelRowsForText(draggedText?.content);
          const mouseCol = x / cellWidth;
          const mouseRow = y / cellHeight;
          const preferredCol = Math.max(0, Math.min(gridCols - textWidth, Math.round(mouseCol - textWidth / 2)));
          const preferredRow = Math.max(0, Math.min(gridRows - textHeight, Math.round(mouseRow - textHeight / 2)));
          
          // Check for collisions with connectors and other text
          const hasCollision = panelConfig.connectors.some(conn => {
            const connRow = conn.position.row;
            const connCol = conn.position.col;
            const span = getConnectorSpanCells(conn);
            const connRight = connCol + (span.width || 9);
            const connBottom = connRow + (span.height || 10);
            
            return !(preferredCol + textWidth < connCol || 
                    preferredCol > connRight ||
                    preferredRow + textHeight < connRow || 
                    preferredRow > connBottom);
          }) || visibleCustomTextItems.some(text => {
            if (text.id === draggedText.id) return false; // Don't check against self
            const textRow = text.position.row;
            const textCol = text.position.col;
            const otherW = estimateLabelColsForText(text.content);
            const textRight = textCol + otherW;
            const textBottom = textRow + estimateLabelRowsForText(text.content);
            
            return !(preferredCol + textWidth < textCol || 
                    preferredCol > textRight ||
                    preferredRow + textHeight < textRow || 
                    preferredRow > textBottom);
          });
          
          const bumpCol = Math.max(2, Math.round(textWidth * 0.25));
          const centeredCol = hasCollision ? preferredCol + bumpCol : preferredCol; // Shift right if collision
          const centeredRow = hasCollision ? preferredRow + textHeight : preferredRow; // Shift down if collision
          
          // Final collision check with the actual position
          const finalCollision = panelConfig.connectors.some(conn => {
            const connRow = conn.position.row;
            const connCol = conn.position.col;
            const span = getConnectorSpanCells(conn);
            const connRight = connCol + (span.width || 9);
            const connBottom = connRow + (span.height || 10);
            
            return !(centeredCol + textWidth < connCol || 
                    centeredCol > connRight ||
                    centeredRow + textHeight < connRow || 
                    centeredRow > connBottom);
          }) || visibleCustomTextItems.some(text => {
            if (text.id === draggedText.id) return false; // Don't check against self
            const textRow = text.position.row;
            const textCol = text.position.col;
            const otherW = estimateLabelColsForText(text.content);
            const textRight = textCol + otherW;
            const textBottom = textRow + estimateLabelRowsForText(text.content);
            
            return !(centeredCol + textWidth < textCol || 
                    centeredCol > textRight ||
                    centeredRow + textHeight < textRow || 
                    centeredRow > textBottom);
          });
          
          // Update text position only if no collision
          if (!finalCollision) {
            setPanelConfig(prev => ({
              ...prev,
              customText: prev.customText.map(text => 
                text.id === draggedText.id 
                  ? (() => {
                      // If label is linked to a connector, store an offset so it moves with the connector.
                      if (text.linkedConnectorId) {
                        const linked = prev.connectors.find((c) => c.id === text.linkedConnectorId);
                        if (linked) {
                          const span = getConnectorSpanCells(linked);
                          const connCol = linked.position?.col ?? 0;
                          const connRow = linked.position?.row ?? 0;
                          const idealLeft = connCol + span.cutoutWidth / 2 - textWidth / 2;
                          const dCol = centeredCol - idealLeft;
                          const dRow = centeredRow - connRow;
                          return {
                            ...text,
                            linkedOffset: { dRow, dCol },
                            position: { row: centeredRow, col: centeredCol }
                          };
                        }
                      }
                      return { ...text, position: { row: centeredRow, col: centeredCol } };
                    })()
                  : text
              )
            }));
            console.log('Text placed at:', { row: centeredRow, col: centeredCol });
          } else {
            console.log('Text placement blocked by collision');
          }
        }
      }
      
      setDraggedText(null);
      setIsDragging(false);
    }
  };

  // Handle logo drag end
  const handleLogoDragEnd = (event) => {
    if (draggedLogo && isDragging) {
      event.preventDefault();
      console.log('Logo drag end');
      
      // Get the grid container
      const gridContainer = document.querySelector('[data-grid-container]');
      if (gridContainer) {
        const rect = gridContainer.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        // Check if drop is within grid bounds
        // Use strict bounds so a drop on the very bottom/right edge can't round to row=gridRows / col=gridCols.
        const isWithinGrid = x >= 0 && x < rect.width && y >= 0 && y < rect.height;
        
        if (isWithinGrid) {
          // Calculate grid position based on drop coordinates
          const cellWidth = rect.width / gridCols;
          const cellHeight = rect.height / gridRows;
          
          // Snap logo to grid boundaries
          const { widthCells: logoWidth, heightCells: logoHeight } = getEffectiveLogoCells(panelConfig.customLogo);
          const mouseCol = x / cellWidth;
          const mouseRow = y / cellHeight;
          const maxCol = Math.max(0, gridCols - logoWidth);
          const maxRow = Math.max(0, gridRows - logoHeight);
          const preferredCol = Math.max(0, Math.min(maxCol, Math.round(mouseCol)));
          const preferredRow = Math.max(0, Math.min(maxRow, Math.round(mouseRow)));
          
          // Check for collision with connectors, text, and other logos
          const hasCollision = panelConfig.connectors.some(conn => {
            const connRow = conn.position.row;
            const connCol = conn.position.col;
            const span = getConnectorSpanCells(conn);
            const connRight = connCol + (span.width || 9);
            const connBottom = connRow + (span.height || 10);
            
            return !(preferredCol + logoWidth < connCol || 
                    preferredCol > connRight ||
                    preferredRow + logoHeight < connRow || 
                    preferredRow > connBottom);
          }) || visibleCustomTextItems.some(text => {
            const textRow = text.position.row;
            const textCol = text.position.col;
            const otherW = estimateLabelColsForText(text.content);
            const textRight = textCol + otherW;
            const textBottom = textRow + estimateLabelRowsForText(text.content);
            
            return !(preferredCol + logoWidth < textCol || 
                    preferredCol > textRight ||
                    preferredRow + logoHeight < textRow || 
                    preferredRow > textBottom);
          });
          
          // Shift if collision, but keep inside bounds so the logo can't disappear off-canvas.
          const centeredCol = Math.max(0, Math.min(maxCol, hasCollision ? preferredCol + logoWidth : preferredCol));
          const centeredRow = Math.max(0, Math.min(maxRow, hasCollision ? preferredRow + logoHeight : preferredRow));
          
          // Final collision check with the actual position
          const finalCollision = panelConfig.connectors.some(conn => {
            const connRow = conn.position.row;
            const connCol = conn.position.col;
            const span = getConnectorSpanCells(conn);
            const connRight = connCol + (span.width || 9);
            const connBottom = connRow + (span.height || 10);
            
            return !(centeredCol + logoWidth < connCol || 
                    centeredCol > connRight ||
                    centeredRow + logoHeight < connRow || 
                    centeredRow > connBottom);
          }) || visibleCustomTextItems.some(text => {
            const textRow = text.position.row;
            const textCol = text.position.col;
            const otherW = estimateLabelColsForText(text.content);
            const textRight = textCol + otherW;
            const textBottom = textRow + estimateLabelRowsForText(text.content);
            
            return !(centeredCol + logoWidth < textCol || 
                    centeredCol > textRight ||
                    centeredRow + logoHeight < textRow || 
                    centeredRow > textBottom);
          });
          
          if (!finalCollision) {
            // Update logo position
            setPanelConfig(prev => ({
              ...prev,
              customLogo: {
                ...prev.customLogo,
                position: { row: centeredRow, col: centeredCol }
              }
            }));
            console.log('Logo placed at:', { row: centeredRow, col: centeredCol });
          } else {
            console.log('Logo placement blocked by collision');
          }
        }
      }
      
      setDraggedLogo(null);
      setIsDragging(false);
    }
  };

  // Handle connector removal
  const handleRemoveConnector = (connectorId) => {
    setPanelConfig(prev => ({
      ...prev,
      connectors: prev.connectors.filter(conn => conn.id !== connectorId),
      customText: prev.customText.filter(t => t.linkedConnectorId !== connectorId)
    }));
  };

  // Handle custom logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        // Uploading a logo implies we’re done placing connectors for now.
        setSelectedConnectorType(null);
        setSelectedConnectorFamily(null);
        setShowConnectorPreview(false);
        setDraggedConnector(null);

        // Create a local URL for immediate in-browser rendering (drag/preview)
        const timestamp = Date.now();
        const fileName = `logo_${timestamp}_${file.name}`;
        const localUrl = URL.createObjectURL(file);

        // Also create a *compressed* data URL for server-side proof rendering.
        // Puppeteer cannot load `blob:` URLs and raw base64 can trigger 413 payload limits.
        const dataUrl = await compressImageFileToDataUrl(file, {
          maxDim: 700,
          targetBytes: 180_000
        });
        
        // Calculate image dimensions and set appropriate grid size
        const img = new Image();
        img.onload = () => {
          const aspectRatio = img.width / img.height;
          let gridWidth, gridHeight;
          
          // Calculate grid dimensions based on aspect ratio
          if (aspectRatio > 1.5) {
            // Wide image (e.g., 200x100) - 2:1 ratio
            gridWidth = 6;
            gridHeight = 3;
          } else if (aspectRatio < 0.7) {
            // Tall image (e.g., 150x300) - 1:2 ratio
            gridWidth = 3;
            gridHeight = 6;
          } else {
            // Square or near-square image (e.g., 400x400) - 1:1 ratio
            gridWidth = 6;
            gridHeight = 6;
          }
          
          setPanelConfig(prev => ({
            ...prev,
            customLogo: {
              url: localUrl,
              proofDataUrl: typeof dataUrl === 'string' ? dataUrl : null,
              position: { row: 0, col: 0 }, // Initial position, will be set by drag and drop
              width: gridWidth,
              height: gridHeight,
              sizeMultiplier: 1.0, // Default size multiplier
              fileName: fileName,
              originalWidth: img.width,
              originalHeight: img.height,
              aspectRatio: aspectRatio
            }
          }));
        };
        img.src = localUrl;
      } catch (error) {
        console.error('Logo upload error:', error);
      }
    }
  };

  // Handle custom text addition
  const handleAddCustomText = () => {
    if (customTextInput.trim()) {
      if (customTextInput.length > textFormatting.maxCharacters) {
        alert(`Text too long! Maximum ${textFormatting.maxCharacters} characters allowed for 1.25" width with 0.25" letters.`);
        return;
      }
      
      const newText = {
        id: `text_${Date.now()}`,
        content: customTextInput,
        position: { row: 0, col: 0 } // Will be set by drag and drop
      };
      
      setPanelConfig(prev => ({
        ...prev,
        customText: [...prev.customText, newText]
      }));
      
      setCustomTextInput('');
      setShowTextDialog(false);
    }
  };

  // Handle wire tailing configuration
  const handleWireTailingChange = (connectorId, wireConfig) => {
    setPanelConfig(prev => ({
      ...prev,
      wireTailings: prev.wireTailings.map(tailing => 
        tailing.connectorId === connectorId 
          ? { ...tailing, ...wireConfig }
          : tailing
      )
    }));
  };

  // Initialize wire tailing for a connector
  const initializeWireTailing = (connectorId) => {
    setPanelConfig((prev) => {
      const existingTailing = (Array.isArray(prev.wireTailings) ? prev.wireTailings : []).find(
        (t) => t.connectorId === connectorId
      );

      // If already configured (not "noTail"), leave as-is.
      if (existingTailing && !existingTailing.noTail) return prev;

      // Get the connector to set endA as panel mount
      const connector = (Array.isArray(prev.connectors) ? prev.connectors : []).find(
        (c) => c.id === connectorId
      );

      console.log('Initializing wire tailing for connector:', {
        connectorId,
        connector,
        connectorIdValue: connector?.connectorId,
        connectorType: connector?.type,
        replacingNoTail: Boolean(existingTailing?.noTail)
      });

      const panelConnector = Object.values(compatibilityMatrix).find((conn) => conn.id === connector?.connectorId);
      const compatibleWireTypes = panelConnector?.wireCompatibility || [];

      const isDmxConnector = (conn) => {
        if (!conn) return false;
        if (conn.poles === 5) return true;
        if (typeof conn.id === 'string' && conn.id.startsWith('NC5')) return true;
        if (typeof conn.family === 'string' && conn.family.toLowerCase().includes('5')) return true;
        return false;
      };

      // Defaults:
      // - Power connectors default to PSP12 (if compatible)
      // - DMX cable (PSDMX) ONLY when a 5-pin XLR is present at either end
      // - Otherwise default to PSM
      const purposes = (panelConnector?.purpose || '')
        .split(',')
        .map((p) => p.trim().toLowerCase())
        .filter(Boolean);

      const defaultByPurpose =
        purposes.includes('power') ? 'PSP12' :
        isDmxConnector(panelConnector) ? 'PSDMX' :
        'PSM';

      const defaultWireType =
        compatibleWireTypes.includes(defaultByPurpose)
          ? defaultByPurpose
          : (compatibleWireTypes[0] || null);

      // Auto-pick a compatible cable-mount tail connector when possible
      const compatibleTailConnectors = Object.values(compatibilityMatrix)
        .filter((conn) =>
          conn.mountType === 'cable' &&
          Array.isArray(conn.compatibleWith) &&
          conn.compatibleWith.some((c) => c.id === connector?.connectorId)
        )
        .sort((a, b) => (a.name || a.description || a.id).localeCompare(b.name || b.description || b.id));

      const autoEndB =
        compatibleTailConnectors.length === 1
          ? compatibleTailConnectors[0].id
          : null;

      const nextTailings = (Array.isArray(prev.wireTailings) ? prev.wireTailings : []).filter(
        (t) => t.connectorId !== connectorId
      );
      nextTailings.push({
        connectorId,
        connectors: {
          endA: {
            connector: connector?.connectorId || connector?.id,
            configuration: 'panel-mount' // endA is always panel mount
          },
          endB: {
            connector: autoEndB,
            configuration: 'cable-mount' // endB is always cable mount
          }
        },
        finishing: {},
        wireType: defaultWireType, // Auto-select if only one option
        length: 72, // Default 6 feet (72 inches)
        specialInstructions: ''
      });

      return {
        ...prev,
        wireTailings: nextTailings
      };
    });
  };

  const setNoWireTail = (connectorId) => {
    setPanelConfig((prev) => {
      const nextTailings = (Array.isArray(prev.wireTailings) ? prev.wireTailings : []).filter(
        (t) => t.connectorId !== connectorId
      );
      nextTailings.push({ connectorId, noTail: true });
      return {
        ...prev,
        wireTailings: nextTailings
      };
    });
  };

  // Validate wire tailings - check if all connectors have complete wire configurations
  const validateWireTailings = () => {
    return validateWireTailingsUtil(panelConfig);
  };

  // Run validation whenever panelConfig changes
  useEffect(() => {
    const validation = validateWireTailings();
    setWireTailingValidation(validation);
  }, [panelConfig.connectors, panelConfig.wireTailings]);

  // Prepare panel grid for Puppeteer capture
  const preparePanelGridForPuppeteer = useCallback(async () => {
    try {
      console.log('🔄 [PANEL-SVG-PREP] Preparing panel grid for Puppeteer capture...');
      
      // Get the actual DOM element we want to capture
      const gridContainer = document.querySelector('[data-grid-container]');
      if (!gridContainer) {
        console.error('❌ [PANEL-SVG-PREP] Grid container not found');
        return null;
      }
      
      console.log('✅ [PANEL-SVG-PREP] Found grid container:', gridContainer);
      
      // Get the current grid dimensions (based on selected panel size/gridSize)
      const gridCols = Math.floor(panelConfig.size.width / panelConfig.gridSize);
      const gridRows = Math.floor(panelConfig.size.height / panelConfig.gridSize);
      const cellSize = 20; // 20px per cell for good resolution
      const gridWidth = gridCols * cellSize;
      const gridHeight = gridRows * cellSize;

      // Proof layout sizing (tails above the panel)
      const tailsAreaHeight = 180; // px
      const proofPadding = 20; // px
      const proofWidth = gridWidth;
      const proofHeight = tailsAreaHeight + gridHeight;

      // Scale the proof down so Puppeteer captures the *entire* panel, even on wide grids.
      // The EC2 capture service may use a fixed viewport width; scaling avoids horizontal cropping.
      // Most important: ensure the capture always includes the *entire* panel.
      // Use a conservative max width so even fixed/limited Puppeteer viewports won't crop.
      const maxProofRenderWidth = 1100; // px
      const proofScale = Math.min(1, maxProofRenderWidth / (proofWidth + proofPadding * 2));
      const scaledProofWidth = Math.ceil(proofWidth * proofScale);
      const scaledProofHeight = Math.ceil(proofHeight * proofScale);

      const formatFeetLabel = (lengthInches) => {
        const ft = (Number(lengthInches || 0) / 12);
        if (!Number.isFinite(ft) || ft <= 0) return '';
        const rounded = Math.round(ft * 10) / 10;
        const display = Number.isInteger(rounded) ? String(rounded) : String(rounded);
        return `${display}'`;
      };

      // Build tail overlays (connector + length) aligned to panel connectors
      const tailsHtml = panelConfig.connectors
        .map((connector) => {
          const tailing = panelConfig.wireTailings.find((t) => t.connectorId === connector.id);
          const endBId = tailing?.connectors?.endB?.connector;
          if (!tailing || !endBId) return '';

          const endB = compatibilityMatrix[endBId] || Object.values(compatibilityMatrix).find((c) => c.id === endBId);
          const endBImage = endB?.image || '';
          // Prefer a human-friendly label for the proof (avoid SKU-only labels).
          const endBLabel =
            endB?.description ||
            (endB?.family ? `Neutrik ${endB.family}` : null) ||
            endB?.type ||
            endB?.skuCode ||
            endB?.id ||
            'TAIL';
          const lengthLabel = formatFeetLabel(tailing.length);

          const tailSpan = getGridSpanForFootprint({
            footprint: getPanelConnectorFootprint({ family: connector.family, type: connector.type }),
            gridSizeInches: panelConfig.gridSize
          });
          const centerXPercent = ((connector.position.col + (tailSpan.widthCells / 2)) / gridCols) * 100;

          return `
            <div class="tail-item" style="position:absolute; left:${centerXPercent}%; top:0; transform:translateX(-50%); width:190px; height:${tailsAreaHeight}px; pointer-events:none;">
              <div style="position:absolute; left:50%; top:55px; transform:translateX(-50%); width:2px; height:${tailsAreaHeight - 90}px; background:#222;"></div>
              ${lengthLabel ? `<div style="position:absolute; left:50%; top:138px; transform:translateX(-50%); font-weight:800; font-size:28px; color:#ff2bbf; background:rgba(255,255,255,0.92); padding:4px 10px; border-radius:14px; border:1px solid rgba(0,0,0,0.10); letter-spacing:0.2px;">${lengthLabel}</div>` : ''}
              <div style="position:absolute; left:50%; top:0; transform:translateX(-50%); width:70px; height:70px; background:#fff; border:1px solid rgba(0,0,0,0.2); border-radius:6px; display:flex; align-items:center; justify-content:center; overflow:hidden;">
                ${endBImage ? `<img src="${endBImage}" alt="${endBLabel}" style="width:100%; height:100%; object-fit:contain; padding:6px;" />` : `<div style="font-size:12px; font-weight:700; color:#111;">${endBLabel}</div>`}
              </div>
              <div style="position:absolute; left:50%; top:76px; transform:translateX(-50%); width: 190px; text-align:center; font-size:11px; font-weight:700; color:#111; background:rgba(255,255,255,0.92); padding:3px 6px; border-radius:10px; border:1px solid rgba(0,0,0,0.08); box-sizing:border-box; overflow:hidden; white-space:normal; line-height:1.1; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical;">
                ${endBLabel}
              </div>
            </div>
          `;
        })
        .join('');
      
      // Create a static HTML representation that matches the actual DOM structure
      const staticPanelHTML = `
        <div class="panel-proof-scale-wrapper" style="
          width: ${scaledProofWidth}px;
          height: ${scaledProofHeight}px;
        ">
          <div class="panel-proof-scale-inner" style="
            width: ${proofWidth}px;
            height: ${proofHeight}px;
            transform: scale(${proofScale});
            transform-origin: top left;
          ">
            <div class="panel-proof-container" style="
              position: relative;
              width: ${proofWidth}px;
              height: ${proofHeight}px;
              background: white;
              border: 2px solid #ddd;
              border-radius: 10px;
              overflow: hidden;
            ">
          <div class="tails-area" style="
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: ${tailsAreaHeight}px;
            background: #fff;
          ">
            ${tailsHtml}
          </div>

          <div class="panel-grid-container" style="
            position: absolute;
            left: 0;
            top: ${tailsAreaHeight}px;
            width: ${gridWidth}px;
            height: ${gridHeight}px;
            background: white;
            border-top: 2px solid #ddd;
            display: grid;
            grid-template-columns: repeat(${gridCols}, 1fr);
            grid-template-rows: repeat(${gridRows}, 1fr);
            grid-gap: 1px;
          ">
          ${Array.from({ length: gridRows }, (_, row) => 
            Array.from({ length: gridCols }, (_, col) => 
              `<div class="grid-cell" style="
                background: ${(row + col) % 2 === 0 ? '#f8f9fa' : 'white'};
                border: 1px solid #e0e0e0;
                min-height: ${cellSize}px;
                min-width: ${cellSize}px;
              "></div>`
            ).join('')
          ).join('')}
          
          ${panelConfig.connectors.map(connector => {
            const { position, type, image } = connector;
            const footprint = getPanelConnectorFootprint({ family: connector.family, type: connector.type });
            const placement = getPlacementGridSpanForFootprint({ footprint, gridSizeInches: panelConfig.gridSize });
            const cutout = getCutoutGridSpanForFootprint({ footprint, gridSizeInches: panelConfig.gridSize });
            const pw = placement.widthCells;
            const ph = placement.heightCells;
            const cw = cutout.widthCells;
            const ch = cutout.heightCells;

            // Align with grid cell boundaries with small margins
            const left = (position.col / gridCols) * 100;
            const top = (position.row / gridRows) * 100;
            const width = ((pw * 0.98) / gridCols) * 100; // 98% of full width
            const height = ((ph * 0.98) / gridRows) * 100; // 98% of full height
            const cutLeft = ((pw - cw) / 2 / pw) * 100;
            const cutTop = ((ph - ch) / 2 / ph) * 100;
            const cutW = (cw / pw) * 100;
            const cutH = (ch / ph) * 100;
            const cutRadius = '4px'; // Builder UI uses square cutout frames for every connector type
            
            return `
              <div class="connector-overlay" style="
                position: absolute;
                left: ${left}%;
                top: ${top}%;
                width: ${width}%;
                height: ${height}%;
                background: white;
                border: 2px solid #000;
                border-radius: 2px;
                overflow: hidden;
                z-index: 10;
              ">
                <div style="
                  position:absolute;
                  left:${cutLeft}%;
                  top:${cutTop}%;
                  width:${cutW}%;
                  height:${cutH}%;
                  border:1px dashed rgba(25,118,210,0.85);
                  border-radius:${cutRadius};
                  background:rgba(255,255,255,0.35);
                  pointer-events:none;
                "></div>
                <div style="
                  position:absolute;
                  left:${cutLeft}%;
                  top:${cutTop}%;
                  width:${cutW}%;
                  height:${cutH}%;
                  display:flex;
                  align-items:center;
                  justify-content:center;
                ">
                  <img src="${image}" alt="${type}" style="
                    max-width: 100%;
                    max-height: 100%;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                    background-color: white;
                    border: 1px solid rgba(0, 0, 0, 0.65);
                    border-radius: ${cutRadius};
                    padding: 2px;
                  ">
                </div>
                <div style="
                  position: absolute;
                  bottom: -20px;
                  left: 50%;
                  transform: translateX(-50%);
                  font-size: 10px;
                  color: #666;
                  white-space: nowrap;
                ">(${position.col},${position.row})</div>
              </div>
            `;
          }).join('')}
          
          ${panelConfig.customText
            .filter((t) => !t.linkedConnectorId || autoLabelConnectors)
            .map(text => {
            const escapeHtml = (value) =>
              String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');

            const isReferenceLabel = (t) => {
              if (!t) return false;
              if (t.linkedConnectorId) return true;
              if (typeof t.id === 'string' && t.id.startsWith('label_')) return true;
              const c = String(t.content || '');
              return /^#\d+\s*\|/.test(c);
            };

            const isRefLabel = isReferenceLabel(text);

            const linkedConnector = text.linkedConnectorId
              ? panelConfig.connectors.find((c) => c.id === text.linkedConnectorId)
              : null;

            const labelCols = estimateLabelColsForText(text.content);
            const labelRows = estimateLabelRowsForText(text.content);

            const labelOffset =
              text.linkedOffset || {
                dRow: -(labelRows + 1),
                dCol: 0
              };

            const defaultRow = text.position?.row || 0;
            const defaultCol = text.position?.col || 0;

            const linkedSpan = linkedConnector ? getConnectorSpanCells(linkedConnector) : null;
            const cutoutW = linkedSpan?.cutoutWidth ?? linkedSpan?.width ?? 9;

            const linkedTopRow = linkedConnector
              ? Math.max(
                  0,
                  Math.min(
                    gridRows - labelRows,
                    (linkedConnector.position?.row ?? 0) + (labelOffset.dRow ?? -(labelRows + 1))
                  )
                )
              : defaultRow;

            const connCol = linkedConnector?.position?.col ?? 0;
            const linkedCenterCol = connCol + cutoutW / 2 + (labelOffset.dCol ?? 0);

            const leftPct = linkedConnector ? (linkedCenterCol / gridCols) * 100 : (defaultCol / gridCols) * 100;
            const topPct = (linkedConnector ? linkedTopRow : defaultRow) / gridRows * 100;
            const widthPct = ((labelCols * 0.97) / gridCols) * 100;
            const heightPct = ((labelRows * 0.93) / gridRows) * 100;

            const innerLeft = linkedConnector ? 'translateX(-50%)' : '';

            // Make engraved text more legible in the proof (fill the box better).
            // Reference labels can be a touch smaller since they’re visual-only.
            const fontPx = isRefLabel
              ? Math.max(8, Math.min(13, Math.round(cellSize * 0.52)))
              : Math.max(10, Math.min(16, Math.round(cellSize * 0.72)));
            
            return `
              <div class="text-overlay" style="
                position: absolute;
                left: ${leftPct}%;
                top: ${topPct}%;
                width: ${widthPct}%;
                height: ${heightPct}%;
                transform: ${innerLeft};
                z-index: 15;
                pointer-events: none;
                box-sizing: border-box;
                /* Proof: reference labels are visual-only (not printed) */
                background: transparent;
                border: ${isRefLabel ? '1px dashed rgba(0,0,0,0.35)' : '1px solid rgba(0,0,0,0.15)'};
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
              ">
                <div style="
                  width: 100%;
                  height: 100%;
                  display:flex;
                  align-items:center;
                  justify-content:center;
                  text-align:center;
                  font-family: Roboto, Arial, sans-serif;
                  font-weight: 700;
                  font-style: normal;
                  font-size: ${fontPx}px;
                  line-height: ${isRefLabel ? 1.05 : 1.0};
                  color: ${isRefLabel ? 'rgba(0,0,0,0.75)' : '#000'};
                  background: ${isRefLabel ? 'rgba(255, 255, 255, 0.55)' : '#ffffff'};
                  border-radius: 2px;
                  border: ${isRefLabel ? '1px dashed rgba(0,0,0,0.35)' : '1px solid rgba(0,0,0,0.25)'};
                  padding: ${isRefLabel ? '1px' : '3px 6px'};
                  overflow: hidden;
                  white-space: ${isRefLabel ? 'normal' : 'nowrap'};
                  word-break: ${isRefLabel ? 'break-word' : 'normal'};
                  hyphens: ${isRefLabel ? 'auto' : 'manual'};
                  text-overflow: ${isRefLabel ? 'clip' : 'ellipsis'};
                ">${escapeHtml(text.content)}</div>
              </div>
            `;
          }).join('')}
          
          ${panelConfig.customLogo && (
            `<div class="logo-overlay" style="
              position: absolute;
              left: ${(panelConfig.customLogo.position.col / gridCols) * 100}%;
              top: ${(panelConfig.customLogo.position.row / gridRows) * 100}%;
              width: ${((getEffectiveLogoCells(panelConfig.customLogo).widthCells / gridCols) * 100)}%;
              height: ${((getEffectiveLogoCells(panelConfig.customLogo).heightCells / gridRows) * 100)}%;
              z-index: 5;
            ">
              <img src="${panelConfig.customLogo.proofDataUrl || panelConfig.customLogo.url}" alt="Logo" style="
                width: 100%;
                height: 100%;
                object-fit: contain;
                filter: grayscale(100%) contrast(170%) brightness(0.9);
              ">
            </div>`
          )}
          </div>
            </div>
          </div>
        </div>
      `;
      
      // Create a complete HTML document for Puppeteer
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=${Math.max(1200, Math.ceil(scaledProofWidth + 120))}, initial-scale=1.0">
          <title>Panel Grid - Desktop Quality</title>
          <style>
            body { 
              margin: 0; 
              padding: 0; 
              background: white; 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              display: flex;
              justify-content: center;
              align-items: flex-start;
              min-height: 100vh;
            }
            .desktop-capture-container {
              display: inline-block;
              width: ${Math.ceil(scaledProofWidth + 80)}px;
              overflow: visible;
              margin: 0 auto;
              padding: 16px 16px 10px;
              background: white;
              border-radius: 8px;
              box-shadow: none;
            }
            .panel-grid-container {
              position: relative;
              width: ${gridWidth}px;
              height: ${gridHeight}px;
              background: white;
              border: 2px solid #ccc;
              display: grid;
              grid-template-columns: repeat(${gridCols}, 1fr);
              grid-template-rows: repeat(${gridRows}, 1fr);
              grid-gap: 1px;
            }
            .grid-cell {
              background: #f8f9fa;
              border: 1px solid #e0e0e0;
              min-height: ${cellSize}px;
              min-width: ${cellSize}px;
            }
            .connector-overlay {
              position: absolute;
              background: #e8f5e8;
              border: 2px solid #4caf50;
              border-radius: 4px;
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 10;
            }
            .text-overlay {
              position: absolute;
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 5;
            }
            .logo-overlay {
              position: absolute;
              z-index: 15;
            }
          </style>
        </head>
        <body>
          <div id="root">
          <div class="desktop-capture-container">
            <h2 style="text-align: center; margin-bottom: 20px; color: #333;">
              Panel Proof
            </h2>
            ${staticPanelHTML}
            <div style="margin-top: 15px; padding: 15px; background: #f5f5f5; border-radius: 6px; font-size: 14px;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span style="font-weight: 500; color: #333;">Grid Size:</span>
                <span style="color: #666;">${gridCols} × ${gridRows} cells (${panelConfig.size.width}" × ${panelConfig.size.height}")</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span style="font-weight: 500; color: #333;">Dimensions:</span>
                <span style="color: #666;">${gridWidth}px × ${gridHeight}px</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span style="font-weight: 500; color: #333;">Connectors:</span>
                <span style="color: #666;">${panelConfig.connectors.length}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span style="font-weight: 500; color: #333;">Text Elements:</span>
                <span style="color: #666;">${
                  panelConfig.customText.filter(
                    (t) => !t.linkedConnectorId || autoLabelConnectors
                  ).length
                }</span>
              </div>
              ${(() => {
                const escapeHtml = (value) =>
                  String(value ?? '')
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#39;');

                const isReferenceLabel = (t) => {
                  if (!t) return false;
                  if (t.linkedConnectorId) return true;
                  if (typeof t.id === 'string' && t.id.startsWith('label_')) return true;
                  const c = String(t.content || '');
                  return /^#\d+\s*\|/.test(c);
                };

                const engraved = (panelConfig.customText || [])
                  .filter((t) => !isReferenceLabel(t))
                  .map((t) => String(t.content || '').trim())
                  .filter(Boolean);

                if (engraved.length === 0) return '';

                return `
                  <div style="margin-top: 10px;">
                    <div style="font-weight: 500; color: #333; margin-bottom: 6px;">Engraved Custom Text:</div>
                    <div style="color:#666; font-size: 13px; line-height: 1.4;">
                      ${engraved.map((t) => `<div style="margin-bottom:2px;">• ${escapeHtml(t)}</div>`).join('')}
                    </div>
                  </div>
                `;
              })()}
              <div style="display: flex; justify-content: space-between;">
                <span style="font-weight: 500; color: #333;">Custom Logo:</span>
                <span style="color: #666;">${panelConfig.customLogo ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
          </div>
        </body>
        </html>
      `;
      
      console.log('✅ [PANEL-SVG-PREP] Panel grid prepared for Puppeteer with dimensions:', { gridWidth, gridHeight });
      return htmlContent;
    } catch (error) {
      console.error('❌ [PANEL-SVG-PREP] Error preparing panel grid:', error);
      return null;
    }
  }, [panelConfig, autoLabelConnectors]);

  // Puppeteer-based image capture function
  const capturePanelImageWithPuppeteer = useCallback(async (sku) => {
    console.log('🔄 [PANEL-PUPPETEER] Starting Puppeteer-based panel capture...');
    
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const outputFileName = `${sku}_${timestamp}.png`;
      
      // Prepare the panel grid HTML for capture
      const panelGridHTML = await preparePanelGridForPuppeteer();
      if (!panelGridHTML) {
        console.error('❌ [PANEL-PUPPETEER] Failed to prepare panel grid HTML');
        return null;
      }
      
      console.log('📄 [PANEL-PUPPETEER] Panel grid HTML length:', panelGridHTML.length);
      
      // Call the server-side capture endpoint
      if (isDev) console.log('📡 [PANEL-PUPPETEER] Sending request to:', API_ENDPOINTS.EC2_IMAGE_GENERATOR + '/api/capture-svg');
      
      const response = await fetch(`${API_ENDPOINTS.EC2_IMAGE_GENERATOR}/api/capture-svg`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          htmlContent: panelGridHTML,
          outputFileName: outputFileName,
          captureType: 'panel-grid',
          elementSelector: '.desktop-capture-container',
          // EC2 capture service: force HTML injection into live page (otherwise it may screenshot the site root).
          captureStrategy: 'render-local',
          contentType: 'html'
        })
      });
      
      if (isDev) console.log('📡 [PANEL-PUPPETEER] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        if (isDev) console.error('📡 [PANEL-PUPPETEER] Error response body:', errorText);
        throw new Error(`Server responded with ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ [PANEL-PUPPETEER] Puppeteer capture successful:', result.s3Url);
        return result.s3Url;
      } else {
        throw new Error(result.error || 'Unknown error from server');
      }
    } catch (error) {
      console.error('❌ [PANEL-PUPPETEER] Error during Puppeteer capture:', error);
      return null;
    }
  }, [preparePanelGridForPuppeteer]);

  // Register with the client image capture service
  const captureFnRef = useRef(capturePanelImageWithPuppeteer);
  useEffect(() => {
    captureFnRef.current = capturePanelImageWithPuppeteer;
  }, [capturePanelImageWithPuppeteer]);

  useEffect(() => {
    const componentId = 'panel-builder';
    if (isDev) console.log('🔧 [PANEL-CAPTURE] Registering capture function for:', componentId);

    const stableCaptureFunction = async () => {
      if (isDev) console.log('🎯 [PANEL-CAPTURE] Capture function called for:', componentId);
      try {
        if (isDev) console.log('🔄 [PANEL-CAPTURE] Starting panel image capture...');
        await new Promise((resolve) => setTimeout(resolve, 100));
        const sku = generatePanelSKUWithTimestamp(panelConfig);
        return await captureFnRef.current(sku);
      } catch (error) {
        console.error('❌ [PANEL-CAPTURE] Error capturing panel image:', error);
        return null;
      }
    };

    clientImageCaptureService.registerCaptureFunction(componentId, stableCaptureFunction);
    return () => {
      if (isDev) console.log('🗑️ [PANEL-CAPTURE] Unregistering capture function for:', componentId);
      clientImageCaptureService.unregisterCaptureFunction(componentId);
    };
  }, [isDev]);

  // Generate panel image for BigCommerce using Puppeteer
  const generatePanelImage = async () => {
    try {
      const sku = generatePanelSKUWithTimestamp(panelConfig);
      const imageUrl = await capturePanelImageWithPuppeteer(sku);
      return imageUrl;
    } catch (error) {
      console.error('Panel image generation error:', error);
      return null;
    }
  };

  // Handle add to cart with BigCommerce integration
  const [bigCommerceLoading, setBigCommerceLoading] = useState(false);
  const [bigCommerceMessage, setBigCommerceMessage] = useState('');
  const [bigCommerceSuccess, setBigCommerceSuccess] = useState(false);
  const [cartUrl, setCartUrl] = useState('');
  
  // Image generation popup state
  const [imageGenerationPopup, setImageGenerationPopup] = useState(false);
  const [imageGenerationProgress, setImageGenerationProgress] = useState(0);
  const progressIntervalRef = useRef(null);
  const [proofImageUrl, setProofImageUrl] = useState(null);
  const [wireLengthUnit, setWireLengthUnit] = useState('ft'); // 'in' or 'ft'

  const handleTailingFinishingChange = useCallback(
    (connectorId, tailing, finishing) => {
      const nextFinishing = finishing || {};
      const hasCustom = finishingUsesCustomHeatShrink(nextFinishing);
      const sharedCustom = nextFinishing.customHeatShrink;

      setPanelConfig((prev) => {
        const nextTailings = (Array.isArray(prev.wireTailings) ? prev.wireTailings : []).map((t) => {
          if (t.connectorId !== connectorId) return t;
          return { ...t, ...tailing, finishing: nextFinishing };
        });

        // If the user is editing custom heat shrink (text/logo), it should apply to all tails.
        // We persist the latest shared config and copy it into every tail that is using custom heat shrink
        // so pricing + proof generation remain consistent.
        if (hasCustom && sharedCustom) {
          const propagated = nextTailings.map((t) => {
            if (t?.noTail) return t;
            const tHasCustom = finishingUsesCustomHeatShrink(t.finishing);
            if (!tHasCustom) return t;
            return {
              ...t,
              finishing: { ...(t.finishing || {}), customHeatShrink: sharedCustom }
            };
          });
          return {
            ...prev,
            tailSharedCustomHeatShrink: sharedCustom,
            wireTailings: propagated
          };
        }

        // If there's already a shared custom HS config, keep tailings in sync when a tail is custom.
        const existingShared = prev.tailSharedCustomHeatShrink;
        if (existingShared) {
          const synced = nextTailings.map((t) => {
            if (t?.noTail) return t;
            const tHasCustom = finishingUsesCustomHeatShrink(t.finishing);
            if (!tHasCustom) return t;
            return {
              ...t,
              finishing: { ...(t.finishing || {}), customHeatShrink: existingShared }
            };
          });
          return { ...prev, wireTailings: synced };
        }

        return { ...prev, wireTailings: nextTailings };
      });
    },
    [finishingUsesCustomHeatShrink]
  );

  const handleAddToCart = async () => {
    if (isDev) console.log('=== PANEL ADD TO CART BUTTON CLICKED ===');
    const cartWindow = openCartTabDuringGesture();
    setBigCommerceLoading(true);
    setBigCommerceMessage('');
    setBigCommerceSuccess(false);
    setCartUrl('');
    
    try {
      // Generate panel image first
      const imageUrl = await generatePanelImage();
      const sku = generatePanelSKUWithTimestamp(panelConfig);

      // Persist SKU in config for downstream (e.g. custom heat shrink asset paths)
      setPanelConfig((prev) => ({ ...prev, sku }));
      
      // Use Panel-specific BigCommerce endpoint (kept separate from cable flow)
      const result = await panelBigCommerceService.addToCart(
        { ...panelConfig, sku },
        panelConfig.totalPrice,
        imageUrl
      );
      
      if (isDev) console.log('=== PANEL BIGCOMMERCE RESULT ===', result);
      
      if (result.success) {
        if (isDev) console.log('=== SUCCESS: Panel product created successfully ===');
        setBigCommerceMessage(`Panel created! SKU: ${result.result.sku}`);
        setBigCommerceSuccess(true);
        setCartUrl(result.result.cart_url || 'https://www.nlfxpro.com/cart.php');
        
        // Navigate pre-opened cart tab (opened on click to avoid popup blockers)
        const redirectUrl = result.result.redirect_url || result.result.cart_url;
        if (isDev) console.log('=== AUTO-OPENING CART ===', redirectUrl);

        if (redirectUrl) {
          const navigated = navigateCartTab(cartWindow, redirectUrl);
          if (navigated) {
            setBigCommerceMessage(
              `Panel created and added to cart! SKU: ${result.result.sku}`
            );
          } else {
            if (isDev) console.log('Popup blocked - showing fallback message');
            setBigCommerceMessage(
              `Panel created! Popup blocked - use "Add to Cart" button to add item.`
            );
          }
        }
      } else {
        throw new Error(result.error || 'Unknown error occurred');
      }
      
    } catch (error) {
      console.error('❌ Panel add to cart error:', error);
      setBigCommerceMessage(`Failed to add panel to cart: ${error.message}`);
      setBigCommerceSuccess(false);
      closeCartTab(cartWindow);
    } finally {
      setBigCommerceLoading(false);
    }
  };

  // Render grid
  // Render the grid background
  const renderGrid = () => {
    const grid = [];
    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        grid.push(
          <Box
            key={`${row}-${col}`}
            sx={{
              width: `${100 / gridCols}%`,
              height: `${100 / gridRows}%`,
              border: '1px solid #e0e0e0',
              backgroundColor: 'transparent',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: selectedConnectorType ? '#f0f8ff' : '#f5f5f5'
              }
            }}
          />
        );
      }
    }
    return grid;
  };

  // Render connector overlays (single draggable images)
  const renderConnectorOverlays = () => {
    return panelConfig.connectors.map(connector => {
      // Hide the connector that's being dragged
      const isBeingDragged = isDragging && draggedConnector && draggedConnector.id === connector.id;
      const { width: spanW, height: spanH, cutoutWidth, cutoutHeight, footprint } = getConnectorSpanCells(connector);
      
      return (
        <Box
          key={connector.id}
          draggable
          onDragStart={(e) => handleDragStart(connector, e)}
          onTouchStart={(e) => handleConnectorTouchStart(connector, e)}
          onTouchMove={handleConnectorTouchMove}
          onTouchEnd={handleConnectorTouchEnd}
          onDragEnd={(e) => handleDragEnd(e)}
          sx={{
            position: 'absolute',
            // Position connector at exact grid cell boundaries with tighter margin
            top: `${(connector.position.row / gridRows) * 100 + 0.05}%`,
            left: `${(connector.position.col / gridCols) * 100 + 0.05}%`,
            width: `${(spanW / gridCols) * 100}%`,
            height: `${(spanH / gridRows) * 100}%`,
            display: isBeingDragged ? 'none' : 'block', // Hide during drag
            cursor: 'grab',
            userSelect: 'none',
            touchAction: 'none',
            zIndex: 10,
            '&:active': {
              cursor: 'grabbing'
            }
          }}
        >
        {/* Cutout guide (true hole size), centered inside the placement keepout box */}
        <Box
          sx={{
            position: 'absolute',
            left: `${((spanW - cutoutWidth) / 2 / spanW) * 100}%`,
            top: `${((spanH - cutoutHeight) / 2 / spanH) * 100}%`,
            width: `${(cutoutWidth / spanW) * 100}%`,
            height: `${(cutoutHeight / spanH) * 100}%`,
            border: '1px dashed rgba(25, 118, 210, 0.85)',
            borderRadius: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.35)',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        />

        {connector.image ? (
          <Box
            sx={{
              position: 'absolute',
              left: `${((spanW - cutoutWidth) / 2 / spanW) * 100}%`,
              top: `${((spanH - cutoutHeight) / 2 / spanH) * 100}%`,
              width: `${(cutoutWidth / spanW) * 100}%`,
              height: `${(cutoutHeight / spanH) * 100}%`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2
            }}
          >
            <img
              src={connector.image}
              alt={connector.type}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                backgroundColor: 'white',
                border: '1px solid rgba(0,0,0,0.65)',
                borderRadius: '3px',
                padding: '2px'
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              position: 'absolute',
              left: `${((spanW - cutoutWidth) / 2 / spanW) * 100}%`,
              top: `${((spanH - cutoutHeight) / 2 / spanH) * 100}%`,
              width: `${(cutoutWidth / spanW) * 100}%`,
              height: `${(cutoutHeight / spanH) * 100}%`,
              backgroundColor: '#4caf50',
              border: '2px solid #000',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '0.6rem',
              fontWeight: 'bold',
              zIndex: 2
            }}
          >
            {connector.type.substring(0, 3).toUpperCase()}
          </Box>
        )}
        
        {/* Grid coordinates display - bottom left */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 4,
            left: 4,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '4px 6px',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            zIndex: 15,
            pointerEvents: 'none',
            userSelect: 'none',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
          }}
        >
          ({connector.position.row},{connector.position.col})
        </Box>
        
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveConnector(connector.id);
          }}
          sx={{ 
            position: 'absolute', 
            top: -1, 
            right: -1,
            backgroundColor: '#B32B2B',
            color: 'white',
            width: '12px !important',
            height: '12px !important',
            borderRadius: '50%',
            zIndex: 20,
            minWidth: '12px !important',
            minHeight: '12px !important',
            padding: '0 !important',
            '&:hover': { backgroundColor: '#B32B2B' },
            '& .MuiSvgIcon-root': {
              fontSize: '8px !important',
              width: '8px !important',
              height: '8px !important'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        </Box>
      );
    });
  };

  // Render custom text overlays
  const renderTextOverlays = () => {
    return visibleCustomTextItems.map(text => {
      const isRefLabel = isConnectorReferenceLabel(text);
      const linked = text.linkedConnectorId
        ? panelConfig.connectors.find((c) => c.id === text.linkedConnectorId)
        : null;

      const textHCells = estimateLabelRowsForText(text.content);
      const labelOffset =
        text.linkedOffset || {
          dRow: -(textHCells + 1),
          dCol: 0
        };
      const defaultRow = text.position?.row || 0;
      const defaultCol = text.position?.col || 0;

      // If linked, anchor the label to the connector so it moves with it.
      // We position using the connector's center X so the label is always centered above/below the connector image.
      const linkedSpan = linked ? getConnectorSpanCells(linked) : null;
      const textWCells = estimateLabelColsForText(text.content);

      const linkedTopRow = linked
        ? Math.max(
            0,
            Math.min(
              gridRows - textHCells,
              (linked.position?.row ?? 0) + (labelOffset.dRow ?? -(textHCells + 1))
            )
          )
        : defaultRow;

      const connCol = linked?.position?.col ?? 0;
      const cutoutW = linkedSpan?.cutoutWidth ?? linkedSpan?.width ?? 9;
      const linkedCenterCol =
        connCol + cutoutW / 2 + (labelOffset.dCol ?? 0);

      const linkedLeftPct = linked ? (linkedCenterCol / gridCols) * 100 : (defaultCol / gridCols) * 100;
      
      return (
        <Box
          key={text.id}
          sx={{
            position: 'absolute',
            containerType: 'inline-size',
            top: `${(linked ? linkedTopRow : defaultRow) / gridRows * 100}%`,
            left: `${linkedLeftPct}%`,
            // Slightly smaller than full grid allocation to avoid overlapping grid dots
            width: `${((textWCells * 0.97) / gridCols) * 100}%`,
            height: `${((textHCells * 0.93) / gridRows) * 100}%`,
            overflow: 'hidden',
            boxSizing: 'border-box',
            ...(linked
              ? {
                  transform: 'translateX(-50%)'
                }
              : {
                  // Center the text within its grid allocation (unlinked text)
                  marginLeft: `${(0.1 / gridCols) * 100}%`,
                  marginTop: `${(0.1 / gridRows) * 100}%`
                }),
            display: draggedText?.id === text.id ? 'none' : 'flex', // Hide during drag
            alignItems: 'stretch',
            justifyContent: 'center',
            cursor: 'grab',
            userSelect: 'none',
            touchAction: 'none',
            zIndex: 15,
            // Make connector reference labels look "non-engraved" vs true Custom Text.
            ...(isRefLabel
              ? {
                  backgroundColor: 'transparent',
                  border: '1px dashed rgba(0,0,0,0.35)'
                }
              : {
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(0,0,0,0.15)'
                }),
            '&:active': {
              cursor: 'grabbing'
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (document.activeElement) document.activeElement.blur();

            const rect = e.currentTarget.getBoundingClientRect();
            const offsetX = e.clientX - (rect.left + rect.width / 2);
            const offsetY = e.clientY - (rect.top + rect.height / 2);

            setDraggedText(text);
            setDragStartPosition({ x: e.clientX, y: e.clientY });
            setDragOffset({ x: offsetX, y: offsetY });
            setIsDragging(true);
            setCurrentDragPosition({ x: e.clientX, y: e.clientY });
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            e.stopPropagation();

            const touch = e.touches[0];
            const rect = e.currentTarget.getBoundingClientRect();
            const offsetX = touch.clientX - (rect.left + rect.width / 2);
            const offsetY = touch.clientY - (rect.top + rect.height / 2);

            setDraggedText(text);
            setDragStartPosition({ x: touch.clientX, y: touch.clientY });
            setDragOffset({ x: offsetX, y: offsetY });
            setIsDragging(true);
            setCurrentDragPosition({ x: touch.clientX, y: touch.clientY });
          }}
        >
          <Typography
            variant="body2"
            sx={{
              // Scale with label box (cqw/cqh); keep mins readable on wide 150+ column grids
              fontSize: 'clamp(11px, min(5.5cqw, 8cqh), 22px)',
              fontFamily: 'Roboto, Arial, sans-serif',
              fontWeight: 700,
              fontStyle: 'normal',
              textAlign: 'center',
              color: '#000',
              lineHeight: 1.15,
              width: '100%',
              height: '100%',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Custom Text: solid white fill (matches connector block backgrounds).
              // Connector reference labels: ghosted fill to differentiate.
              backgroundColor: isRefLabel ? 'rgba(255, 255, 255, 0.55)' : '#ffffff',
              borderRadius: '2px',
              border: isRefLabel ? '1px dashed rgba(0,0,0,0.35)' : '1px solid rgba(0,0,0,0.25)',
              padding: '4px 6px',
              boxSizing: 'border-box',
              // Engraved Custom Text should stay single-line in the builder.
              whiteSpace: isRefLabel ? 'normal' : 'nowrap',
              wordBreak: isRefLabel ? 'break-word' : 'normal',
              hyphens: isRefLabel ? 'auto' : 'manual',
              textOverflow: isRefLabel ? 'clip' : 'ellipsis',
              overflow: 'hidden'
            }}
          >
            {text.content}
          </Typography>
        </Box>
      );
    });
  };

  // Render custom logo overlay
  const renderLogoOverlay = () => {
    if (!panelConfig.customLogo) return null;
    
    // Use the correct position properties (row/col, not x/y)
    const logoRow = panelConfig.customLogo.position.row || 0;
    const logoCol = panelConfig.customLogo.position.col || 0;
    const { widthCells: logoWCells, heightCells: logoHCells } = getEffectiveLogoCells(panelConfig.customLogo);
    
    return (
      <Box
        draggable
        sx={{
          position: 'absolute',
          // Position logo to align with grid cell boundaries
          top: `${(logoRow / gridRows) * 100}%`,
          left: `${(logoCol / gridCols) * 100}%`,
          // Slightly smaller than full grid allocation to avoid overlapping grid dots
          width: `${((logoWCells * 0.98 / gridCols) * 100)}%`,
          height: `${((logoHCells * 0.98 / gridRows) * 100)}%`,
          // Center the logo within its grid allocation
          marginLeft: `${(0.1 / gridCols) * 100}%`, // Small margin on left
          marginTop: `${(0.1 / gridRows) * 100}%`, // Small margin on top
          display: draggedLogo ? 'none' : 'flex', // Hide during drag
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'grab',
          userSelect: 'none',
          touchAction: 'none',
          zIndex: 15,
          backgroundColor: 'rgba(255, 255, 0, 0.3)',
          border: '1px dashed #000',
          '&:active': {
            cursor: 'grabbing'
          }
        }}
        onDragStart={(e) => {
          e.preventDefault();
          console.log('🎯 Drag start for logo');
          
          // Calculate offset from mouse to logo center
          const rect = e.currentTarget.getBoundingClientRect();
          const offsetX = e.clientX - (rect.left + rect.width / 2); // Center horizontally
          const offsetY = e.clientY - (rect.top + rect.height / 2); // Center vertically
          
          setDraggedLogo(true);
          setDragStartPosition({ x: e.clientX, y: e.clientY });
          setDragOffset({ x: offsetX, y: offsetY });
          setIsDragging(true);
          setCurrentDragPosition({ x: e.clientX, y: e.clientY });
          console.log('🎯 Logo drag state set:', { draggedLogo: true, isDragging: true });
        }}
        onDragEnd={(e) => {
          e.preventDefault();
          console.log('Drag end for logo');
          
          // Get the grid container
          const gridContainer = document.querySelector('[data-grid-container]');
          if (gridContainer) {
            const rect = gridContainer.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            
            // Check if drop is within grid bounds
            const isWithinGrid = x >= 0 && x <= rect.width && y >= 0 && y <= rect.height;
            
            if (isWithinGrid) {
              // Calculate grid position based on drop coordinates
              const cellWidth = rect.width / gridCols;
              const cellHeight = rect.height / gridRows;
              
              // Center the logo on the drop position
              const { widthCells: logoWidth, heightCells: logoHeight } = getEffectiveLogoCells(panelConfig.customLogo);
              const mouseCol = x / cellWidth;
              const mouseRow = y / cellHeight;
              const centeredCol = Math.max(0, Math.min(gridCols - logoWidth, Math.round(mouseCol - logoWidth / 2)));
              const centeredRow = Math.max(0, Math.min(gridRows - logoHeight, Math.round(mouseRow - logoHeight / 2)));
              
              // Check for collision with connectors
              const logoRight = centeredCol + logoWidth;
              const logoBottom = centeredRow + logoHeight;
              
              const hasCollision = panelConfig.connectors.some(conn => {
                const connRow = conn.position.row;
                const connCol = conn.position.col;
                const span = getConnectorSpanCells(conn);
                const connRight = connCol + (span.width || 9);
                const connBottom = connRow + (span.height || 10);
                
                return !(logoRight < connCol || 
                        centeredCol > connRight ||
                        logoBottom < connRow || 
                        centeredRow > connBottom);
              });
              
              if (!hasCollision) {
                // Update logo position
                setPanelConfig(prev => ({
                  ...prev,
                  customLogo: {
                    ...prev.customLogo,
                    position: { row: centeredRow, col: centeredCol }
                  }
                }));
                console.log('Logo placed at:', { row: centeredRow, col: centeredCol });
              } else {
                console.log('Logo placement blocked by collision');
              }
            }
          }
          
          setDraggedLogo(null);
          setIsDragging(false);
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          console.log('Touch start for logo');
          setDraggedLogo(true);
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          console.log('Touch end for logo');
          setDraggedLogo(null);
          
          // Calculate drop position for touch
          const rect = e.currentTarget.closest('[data-grid-container]')?.getBoundingClientRect();
          if (rect) {
            const touch = e.changedTouches[0];
            const x = touch.clientX - rect.left;
            const y = touch.clientY - rect.top;
            
            // Convert to grid coordinates
            const cellWidth = rect.width / gridCols;
            const cellHeight = rect.height / gridRows;
            const col = Math.floor(x / cellWidth);
            const row = Math.floor(y / cellHeight);
            
            // Update logo position
            setPanelConfig(prev => ({
              ...prev,
              customLogo: {
                ...prev.customLogo,
                position: { x: col * panelConfig.gridSize, y: row * panelConfig.gridSize }
              }
            }));
          }
        }}
      >
        <img 
          src={panelConfig.customLogo.url} 
          alt="Custom Logo"
          style={{ 
            maxWidth: '100%', 
            maxHeight: '100%', 
            objectFit: 'contain',
            filter: 'grayscale(100%)' // Show how it will look when laser engraved
          }}
        />
      </Box>
    );
  };

  // Render drag overlay that follows mouse cursor
  const renderDragOverlay = () => {
    if (!isDragging || !currentDragPosition) return null;
    if (!draggedConnector && !draggedText && !draggedLogo) return null;
    
    console.log('Rendering drag overlay for:', draggedConnector?.type, 'with image:', draggedConnector?.image);
    
    // Calculate the same size as the connector on the grid
    const gridContainer = document.querySelector('[data-grid-container]');
    if (!gridContainer) return null;
    
    const rect = gridContainer.getBoundingClientRect();
    // Calculate size based on what's being dragged
    let overlayWidth, overlayHeight;
    if (draggedConnector) {
      const span = getConnectorSpanCells(draggedConnector);
      overlayWidth = (span.width / gridCols) * rect.width;
      overlayHeight = (span.height / gridRows) * rect.height;
    } else if (draggedText) {
      // Text overlay size
      const tw = estimateLabelColsForText(draggedText?.content);
      const th = estimateLabelRowsForText(draggedText?.content);
      overlayWidth = (tw / gridCols) * rect.width;
      overlayHeight = (th / gridRows) * rect.height;
    } else if (draggedLogo) {
      // Logo overlay size (same as text for now)
      overlayWidth = (6 / gridCols) * rect.width;
      overlayHeight = (3 / gridRows) * rect.height;
    }

    console.log('Drag overlay position:', { 
      x: currentDragPosition.x - overlayWidth / 2, 
      y: currentDragPosition.y - overlayHeight / 2,
      width: overlayWidth,
      height: overlayHeight
    });
    
    return (
      <Box
        sx={{
          position: 'fixed',
          top: currentDragPosition.y - overlayHeight / 2,
          left: currentDragPosition.x - overlayWidth / 2,
          width: overlayWidth,
          height: overlayHeight,
          pointerEvents: 'none',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '2px solid #b32b2b',
          borderRadius: '4px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
        }}
      >
        {draggedConnector && (() => {
          const span = getConnectorSpanCells(draggedConnector);
          const pw = span.width;
          const ph = span.height;
          const cw = span.cutoutWidth;
          const ch = span.cutoutHeight;
          const cutLeft = `${((pw - cw) / 2 / pw) * 100}%`;
          const cutTop = `${((ph - ch) / 2 / ph) * 100}%`;
          const cutW = `${(cw / pw) * 100}%`;
          const cutH = `${(ch / ph) * 100}%`;
          const cutRadius = '4px';

          return draggedConnector.image ? (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  left: cutLeft,
                  top: cutTop,
                  width: cutW,
                  height: cutH,
                  border: '1px dashed rgba(25, 118, 210, 0.85)',
                  borderRadius: cutRadius,
                  backgroundColor: 'rgba(255, 255, 255, 0.35)',
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: cutLeft,
                  top: cutTop,
                  width: cutW,
                  height: cutH,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2
                }}
              >
                <img
                  src={draggedConnector.image}
                  alt={draggedConnector.type}
                  onLoad={() => console.log('Drag overlay image loaded successfully')}
                  onError={() => console.log('Drag overlay image failed to load:', draggedConnector.image)}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    backgroundColor: 'white',
                    border: '1px solid rgba(0,0,0,0.65)',
                    borderRadius: cutRadius,
                    padding: '2px'
                  }}
                />
              </Box>
            </>
          ) : (
            <Box
              sx={{
                position: 'absolute',
                left: cutLeft,
                top: cutTop,
                width: cutW,
                height: cutH,
                backgroundColor: '#4caf50',
                border: '2px solid #000',
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.6rem',
                fontWeight: 'bold',
                zIndex: 2
              }}
            >
              {draggedConnector.type.substring(0, 3).toUpperCase()}
            </Box>
          );
        })()}
        {draggedText && (
          <Typography
            variant="body2"
            sx={{
              fontSize: 'clamp(9px, 2.6vw, 12px)',
              fontWeight: 700,
              color: '#000',
              textAlign: 'center',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              hyphens: 'auto',
              overflow: 'hidden',
              lineHeight: 1.05,
              maxWidth: '100%',
              padding: '4px'
            }}
          >
            {draggedText.content}
          </Typography>
        )}
        {draggedLogo && panelConfig.customLogo && (
          <img 
            src={panelConfig.customLogo.url} 
            alt="Custom Logo"
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain',
            filter: 'grayscale(100%) contrast(170%) brightness(0.9)' // Engraving preview: high-contrast B/W
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: nlfxColors.background, py: { xs: 2, md: 4 } }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            bgcolor: nlfxColors.card,
            border: '1px solid rgba(0,0,0,0.08)'
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            NLFX Professional Custom Panel Builder
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Design your panel layout, configure tailings, and add to cart.
          </Typography>
        </Paper>
      
      {/* Progress Stepper */}
      <Stepper 
        activeStep={activeStep} 
        sx={{ 
          mb: 3,
          '& .MuiStepLabel-root .Mui-completed': {
            color: nlfxColors.secondary,
          },
          '& .MuiStepLabel-root .Mui-active': {
            color: nlfxColors.secondary,
          },
          '& .MuiStepLabel-root .MuiStepLabel-label': {
            color: '#666', // Gray for inactive steps
          },
          '& .MuiStepLabel-root .Mui-completed .MuiStepLabel-label': {
            color: nlfxColors.secondary,
          },
          '& .MuiStepLabel-root .Mui-active .MuiStepLabel-label': {
            color: nlfxColors.secondary,
          },
          '& .MuiStepIcon-root': {
            color: '#ccc', // Gray for inactive step icons
            '&.Mui-completed': {
              color: nlfxColors.secondary,
            },
            '&.Mui-active': {
              color: nlfxColors.secondary,
            }
          }
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Panel Size Selection */}
      {activeStep === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Select Panel Size
          </Typography>

          {(() => {
            const renderPresetGrid = (presets) => (
              <Grid container spacing={2}>
                {presets.map((preset) => (
                  <Grid item xs={12} sm={6} md={4} key={preset.name}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border:
                          panelConfig.size.width === preset.width &&
                          panelConfig.size.height === preset.height &&
                          panelConfig.material === preset.material
                            ? '2px solid #4caf50'
                            : '1px solid #e0e0e0',
                        '&:hover': { boxShadow: 4 }
                      }}
                      onClick={() => handlePanelSizeChange(preset)}
                    >
                      <CardContent>
                        <Typography variant="h6">{preset.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {preset.width}" × {preset.height}"
                        </Typography>
                        <Typography variant="caption" display="block">
                          {preset.description}
                        </Typography>
                        <Chip
                          label={preset.material === 'flanged-aluminum' ? 'Flanged Aluminum' : 'Black Anodized'}
                          size="small"
                          color={preset.material === 'flanged-aluminum' ? 'error' : 'default'}
                          sx={{ mt: 1 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            );

            const aluminum = panelSizePresets.filter((p) => p.material === 'flanged-aluminum');
            const black = panelSizePresets.filter((p) => p.material === 'black-anodized');

            return (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Flanged Aluminum
                </Typography>
                {renderPresetGrid(aluminum)}

                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, mt: 3 }}>
                  Black Anodized
                </Typography>
                {renderPresetGrid(black)}
              </Box>
            );
          })()}
          
          {/* Why Flanged vs Flat Video */}
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Why Flanged Aluminum?
            </Typography>
            <Paper elevation={2} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We use flanged aluminum panels instead of flat panels for better durability and professional appearance.
              </Typography>
              <Box sx={{ 
                width: '100%', 
                height: 200, 
                backgroundColor: '#e0e0e0', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderRadius: 1,
                border: '2px dashed #ccc'
              }}>
                <Typography variant="body2" color="text.secondary">
                  📹 Video: Why Flanged vs Flat Panels
                  <br />
                  <small>(Video placeholder - add your explanation video here)</small>
                </Typography>
              </Box>
            </Paper>
          </Box>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Custom Dimensions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Width (inches)"
                  type="number"
                  value={panelConfig.size.width}
                  onChange={(e) => setPanelConfig(prev => ({
                    ...prev,
                    size: { ...prev.size, width: parseFloat(e.target.value) || 0 }
                  }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Height (inches)"
                  type="number"
                  value={panelConfig.size.height}
                  onChange={(e) => setPanelConfig(prev => ({
                    ...prev,
                    size: { ...prev.size, height: parseFloat(e.target.value) || 0 }
                  }))}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}

      {/* Step 2: Connector Selection and Placement */}
      {activeStep === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Add Connectors to Panel
          </Typography>
          
          {/* Connector Selection */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                Select Connector Type
              </Typography>
              {(selectedConnectorType || selectedConnectorFamily) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSelectedConnectorType(null);
                    setSelectedConnectorFamily(null);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setSelectedConnectorType(null);
                    setSelectedConnectorFamily(null);
                  }}
                  sx={{ 
                    color: '#b32b2b',
                    borderColor: '#b32b2b',
                    '&:hover': {
                      borderColor: '#8b2222',
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  Clear Selection
                </Button>
              )}
            </Box>
            <Grid container spacing={2}>
              {connectorFamilies.map((family) => {
                // Get the panel mount variant image
                const panelMountVariant = family.variants.find(variant => variant.mountType === 'panel');
                const imageToShow = panelMountVariant?.image || family.image;
                
                return (
                  <Grid item key={family.id}>
                    <Tooltip
                      title={
                        imageToShow ? (
                          <Box sx={{ p: 1, textAlign: 'center' }}>
                            <img 
                              src={imageToShow} 
                              alt={family.name}
                              style={{ 
                                width: 120, 
                                height: 120, 
                                objectFit: 'contain',
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                padding: '8px'
                              }}
                            />
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'white' }}>
                              {family.name}
                            </Typography>
                          </Box>
                        ) : family.name
                      }
                      arrow
                      placement="top"
                    >
                      <Chip
                        label={family.name}
                        onClick={() => handleConnectorFamilyChipClick(family)}
                        color={selectedConnectorFamily?.id === family.id ? 'error' : 'default'}
                        variant={selectedConnectorFamily?.id === family.id ? 'filled' : 'outlined'}
                        sx={{
                          height: 50, // Increased from default ~40px to 50px
                          fontSize: '1rem', // Increased text size
                          padding: '8px 16px', // Increased padding
                          '& .MuiChip-icon': {
                            width: 40, // Match the image size
                            height: 40,
                            marginRight: '8px'
                          }
                        }}
                        icon={imageToShow ? (
                          <img 
                            src={imageToShow} 
                            alt={family.name}
                            style={{ width: 40, height: 40, objectFit: 'contain' }}
                          />
                        ) : undefined}
                      />
                    </Tooltip>
                  </Grid>
                );
              })}
            </Grid>
            
            {/* Show all panel mount variants for selected family */}
            {selectedConnectorFamily && selectedConnectorFamily.variants && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Choose specific connector:
                </Typography>
                <Grid container spacing={1}>
                  {selectedConnectorFamily.variants
                    .filter(variant => variant.mountType === 'panel')
                    .map((variant) => (
                      <Grid item key={variant.id}>
                        <Tooltip
                          title={
                            variant.image ? (
                              <Box sx={{ p: 1, textAlign: 'center' }}>
                                <img 
                                  src={variant.image} 
                                  alt={variant.name}
                                  style={{ 
                                    width: 120, 
                                    height: 120, 
                                    objectFit: 'contain',
                                    backgroundColor: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    padding: '8px'
                                  }}
                                />
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'white' }}>
                                  {variant.name}
                                </Typography>
                              </Box>
                            ) : variant.name
                          }
                          arrow
                          placement="top"
                        >
                          <Chip
                            label={`${variant.name} (${variant.gender || 'N/A'})`}
                            onClick={() => {
                              // If the same connector is already selected, deselect it
                              if (selectedConnectorType?.connectorId === variant.id) {
                                console.log('🎯 Deselecting connector variant');
                                setSelectedConnectorType(null);
                                return;
                              }
                              
                              // Create a temporary connector for placement
                              const tempConnector = {
                                id: `temp_${Date.now()}`,
                                type: selectedConnectorFamily.name,
                                connectorId: variant.id,
                                position: { row: 0, col: 0 },
                                image: variant.image,
                                family: selectedConnectorFamily.name,
                                description: variant.description || variant.name,
                                gender: variant.gender,
                                poles: variant.poles
                              };
                              console.log('🎯 Selected connector variant:', tempConnector);
                              setSelectedConnectorType(tempConnector);
                            }}
                            sx={{
                              color: selectedConnectorType?.connectorId === variant.id ? '#fff' : '#000',
                              borderColor: '#b32b2b',
                              backgroundColor: selectedConnectorType?.connectorId === variant.id ? '#b32b2b' : 'transparent',
                              height: 60, // Larger than family chips (50px) to show progression
                              fontSize: '1.1rem', // Larger text than family chips (1rem)
                              padding: '10px 20px', // Larger padding than family chips (8px 16px)
                              mb: 1,
                              '&:hover': {
                                borderColor: '#8b2222',
                                backgroundColor: selectedConnectorType?.connectorId === variant.id ? '#8b2222' : '#f5f5f5'
                              },
                              '& .MuiChip-icon': {
                                width: 45, // Larger than family chips (40px) to show progression
                                height: 45,
                                marginRight: '10px' // Increased margin to match larger size
                              }
                            }}
                            variant={selectedConnectorType?.connectorId === variant.id ? 'filled' : 'outlined'}
                            icon={variant.image ? (
                              <img 
                                src={variant.image} 
                                alt={variant.name}
                                style={{ width: 30, height: 30, objectFit: 'contain' }}
                              />
                            ) : undefined}
                          />
                        </Tooltip>
                      </Grid>
                    ))}
                </Grid>
              </Box>
            )}
          </Box>

          {/* Grid Display */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="subtitle2">
                Panel Grid ({gridCols} × {gridRows} cells)
              </Typography>
              {selectedConnectorType && (
                <Typography variant="caption" sx={{ 
                  color: '#d32f2f', 
                  fontWeight: 'bold',
                  display: 'block',
                  mt: 0.5
                }}>
                  🎯 Placing: {selectedConnectorType.description}
                </Typography>
              )}
            </Box>
              
              {/* Zoom and Pan Controls */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {panelConfig.connectors.length > 1 && (
                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mr: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Align
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => alignPlacedConnectors('top')}
                      sx={{ textTransform: 'none' }}
                    >
                      Top
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => alignPlacedConnectors('middle')}
                      sx={{ textTransform: 'none' }}
                    >
                      Middle
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => alignPlacedConnectors('bottom')}
                      sx={{ textTransform: 'none' }}
                    >
                      Bottom
                    </Button>
                  </Box>
                )}
                {/* Clear Selection Button */}
                {selectedConnectorType && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSelectedConnectorType(null)}
                    sx={{ 
                      backgroundColor: '#ffebee',
                      borderColor: '#f44336',
                      color: '#d32f2f',
                      '&:hover': {
                        backgroundColor: '#ffcdd2',
                        borderColor: '#d32f2f'
                      }
                    }}
                  >
                    ✕ Clear Selection
                  </Button>
                )}
                
              </Box>
            </Box>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                backgroundColor: '#fafafa',
                border: '2px solid #4caf50',
                borderRadius: 2
              }}
            >
              <Box
                data-grid-container
                onClick={handleGridClick}
                onDragOver={handleDragOver}
                onDrop={handleDragEnd}
                onMouseMove={(e) => {
                  if (selectedConnectorType) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setPreviewPosition({ x: e.clientX, y: e.clientY });
                    setShowConnectorPreview(true);
                  }
                }}
                onMouseLeave={() => setShowConnectorPreview(false)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                  gridTemplateRows: `repeat(${gridRows}, 1fr)`,
                  aspectRatio: `${gridCols}/${gridRows}`, // Proper aspect ratio based on cell count
                  maxWidth: '100%', // Don't exceed container width
                  maxHeight: '80vh', // Use viewport height for better scaling
                  margin: '0 auto',
                  width: 'auto', // Let width be determined by cell count
                  height: 'auto', // Let height be determined by aspect ratio
                  minHeight: '200px', // Minimum height
                  minWidth: '300px', // Minimum width for very small grids
                  // Dynamic cell size based on viewport
                  '--cell-size': `${currentCellSize}px`,
                  gridGap: '1px', // Small gap between cells for better visibility
                  cursor: selectedConnectorType ? 'crosshair' : 'default',
                  position: 'relative', // Enable absolute positioning for overlays
                  overflow: 'hidden'
                }}
              >
                {renderGrid()}
                {renderConnectorOverlays()}
                {renderTextOverlays()}
                {renderLogoOverlay()}
                {renderDragOverlay()}
              </Box>
            </Paper>
            {/* Placement ghost follows cursor — portal to body so it never participates in grid layout (fixes hover “shrink”) */}
            {typeof document !== 'undefined' &&
              showConnectorPreview &&
              selectedConnectorType &&
              previewPosition &&
              (() => {
                const gridContainer = document.querySelector('[data-grid-container]');
                if (!gridContainer) return null;
                const rect = gridContainer.getBoundingClientRect();
                const previewSpan = getConnectorSpanCells({
                  family: selectedConnectorType.family || selectedConnectorType.name,
                  type: selectedConnectorType.type || selectedConnectorType.name
                });
                const previewWidth = (previewSpan.width / gridCols) * rect.width;
                const previewHeight = (previewSpan.height / gridRows) * rect.height;
                const pw = previewSpan.width;
                const ph = previewSpan.height;
                const cw = previewSpan.cutoutWidth;
                const ch = previewSpan.cutoutHeight;
                const cutLeft = `${((pw - cw) / 2 / pw) * 100}%`;
                const cutTop = `${((ph - ch) / 2 / ph) * 100}%`;
                const cutW = `${(cw / pw) * 100}%`;
                const cutH = `${(ch / ph) * 100}%`;
                const cutRadius = '4px';

                return createPortal(
                  <Box
                    sx={{
                      position: 'fixed',
                      top: previewPosition.y - previewHeight / 2,
                      left: previewPosition.x - previewWidth / 2,
                      width: previewWidth,
                      height: previewHeight,
                      pointerEvents: 'none',
                      zIndex: 10000,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '2px solid #b32b2b',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    {selectedConnectorType.image ? (
                      <>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: cutLeft,
                            top: cutTop,
                            width: cutW,
                            height: cutH,
                            border: '1px dashed rgba(25, 118, 210, 0.85)',
                            borderRadius: cutRadius,
                            backgroundColor: 'rgba(255, 255, 255, 0.35)',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            left: cutLeft,
                            top: cutTop,
                            width: cutW,
                            height: cutH,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2
                          }}
                        >
                          <img
                            src={selectedConnectorType.image}
                            alt={selectedConnectorType.type}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
                        {selectedConnectorType.type?.substring(0, 3).toUpperCase()}
                      </Typography>
                    )}
                  </Box>,
                  document.body
                );
              })()}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {selectedConnectorType ? (
                <>
                  <strong>🎯 Selected: {selectedConnectorType.description}</strong> - Click on grid cells to place this connector. 
                  Click the connector again to deselect it.
                  <br />
                  Drag placed connectors to move them around the panel. Works on mobile devices too!
                  <br />
                  <br />
                  Placement keepout (approx): {selectedPlacementSpan?.footprint?.keepoutMm?.width ?? '—'}mm ×{' '}
                  {selectedPlacementSpan?.footprint?.keepoutMm?.height ?? '—'}mm (shown as {selectedPlacementSpan?.width ?? '—'} ×{' '}
                  {selectedPlacementSpan?.height ?? '—'} grid cells).
                  {' '}
                  Panel cutout (approx): {selectedPlacementSpan?.footprint?.cutoutMm?.width ?? '—'}mm ×{' '}
                  {selectedPlacementSpan?.footprint?.cutoutMm?.height ?? '—'}mm (shown as {selectedPlacementSpan?.cutoutWidth ?? '—'} ×{' '}
                  {selectedPlacementSpan?.cutoutHeight ?? '—'} grid cells).
                  On wide rack panels, grid cells are wider than they are tall—so a square cutout can still look “tall” in grid units.
                </>
              ) : (
                <>
                  <strong>Click to place, drag to move</strong> - Select a connector type above, then click on grid cells to place connectors. 
                  Drag placed connectors to move them around the panel. Works on mobile devices too!
                  <br />
                  <br />
                  Connector placement spacing is based on each family’s footprint (not a single 9×10 box). On wide rack panels, grid cells are wider than they are tall—connector boxes follow that physical aspect ratio.
                </>
              )}
            </Typography>
          </Box>

          {/* Placed connectors + connector label visibility (grid-adjacent) */}
          <Box sx={{ mb: 3 }}>
            {panelConfig.connectors.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Placed Connectors ({panelConfig.connectors.length})
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {panelConfig.connectors.map((connector) => (
                    <Grid item key={connector.id}>
                      <Chip
                        label={`${connector.type} (${connector.position.row},${connector.position.col})`}
                        onDelete={() => handleRemoveConnector(connector.id)}
                        color="error"
                        variant="outlined"
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
            <FormControlLabel
              sx={{ display: 'flex', alignItems: 'flex-start', ml: 0, mr: 0, mb: 1 }}
              control={
                <Switch
                  checked={autoLabelConnectors}
                  onChange={(e) => setAutoLabelConnectors(e.target.checked)}
                />
              }
              label={
                <Box component="span" sx={{ lineHeight: 1.4 }}>
                  Show connector labels
                </Box>
              }
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0, maxWidth: 560, lineHeight: 1.5, pl: 0.5 }}
            >
              <strong>Note:</strong> Connector reference labels are for this builder only—they do{' '}
              <strong>not</strong> appear on the finished panel and are{' '}
              <strong>not</strong> the same as <em>Custom Text</em> (paid engraved lettering below).
            </Typography>
            {connectorReferenceLabelItems.length > 0 && (
              <Box sx={{ mt: 1.5, pl: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                  Connector reference labels
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Separate from Custom Text—included here so you can remove individual labels from the canvas.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {connectorReferenceLabelItems.map((text) => (
                    <Chip
                      key={text.id}
                      label={text.content}
                      size="small"
                      variant="outlined"
                      onDelete={() =>
                        setPanelConfig((prev) => ({
                          ...prev,
                          customText: prev.customText.filter((t) => t.id !== text.id)
                        }))
                      }
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Customization Section */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Customize Your Panel
            </Typography>
            
            {/* Custom Logo */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Custom Logo
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Laser Engraving - not super high resolution
              </Typography>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => document.getElementById('logo-upload').click()}
                sx={{ mr: 2 }}
              >
                Upload Logo
              </Button>
              {panelConfig.customLogo && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <img 
                      src={panelConfig.customLogo.url} 
                      alt="Uploaded Logo" 
                      style={{ 
                        width: 32, 
                        height: 32, 
                        objectFit: 'contain',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        filter: 'grayscale(100%) contrast(170%) brightness(0.9)' // Engraving preview: high-contrast B/W
                      }} 
                    />
                    <Chip
                      label="Logo Added"
                      color="success"
                      onDelete={() => setPanelConfig(prev => ({ ...prev, customLogo: null }))}
                    />
                  </Box>
                  
                  {/* Logo Size Controls */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                      label="Width (cells)"
                      type="number"
                      size="small"
                      value={getEffectiveLogoCells(panelConfig.customLogo).widthCells || 6}
                      onChange={(e) => setPanelConfig(prev => ({
                        ...prev,
                        customLogo: {
                          ...prev.customLogo,
                          ...(function () {
                            const mult = prev.customLogo?.sizeMultiplier || 1.0;
                            const arRaw = Number(prev.customLogo?.aspectRatio);
                            const ar = Number.isFinite(arRaw) && arRaw > 0 ? arRaw : 1;

                            const desiredEffW = Math.max(1, Math.min(gridCols, parseInt(e.target.value) || 6));
                            const desiredEffH = Math.max(1, Math.min(gridRows, Math.round(desiredEffW / ar)));

                            const nextBaseW = Math.max(1, Math.round(desiredEffW / mult));
                            const nextBaseH = Math.max(1, Math.round(desiredEffH / mult));

                            return { width: nextBaseW, height: nextBaseH };
                          })()
                        }
                      }))}
                      inputProps={{ min: 1, max: gridCols }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="Height (cells)"
                      type="number"
                      size="small"
                      value={getEffectiveLogoCells(panelConfig.customLogo).heightCells || 3}
                      onChange={(e) => setPanelConfig(prev => ({
                        ...prev,
                        customLogo: {
                          ...prev.customLogo,
                          ...(function () {
                            const mult = prev.customLogo?.sizeMultiplier || 1.0;
                            const arRaw = Number(prev.customLogo?.aspectRatio);
                            const ar = Number.isFinite(arRaw) && arRaw > 0 ? arRaw : 1;

                            const desiredEffH = Math.max(1, Math.min(gridRows, parseInt(e.target.value) || 3));
                            const desiredEffW = Math.max(1, Math.min(gridCols, Math.round(desiredEffH * ar)));

                            const nextBaseW = Math.max(1, Math.round(desiredEffW / mult));
                            const nextBaseH = Math.max(1, Math.round(desiredEffH / mult));

                            return { width: nextBaseW, height: nextBaseH };
                          })()
                        }
                      }))}
                      inputProps={{ min: 1, max: gridRows }}
                      sx={{ width: 120 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        label="Size Multiplier"
                        type="number"
                        size="small"
                        value={panelConfig.customLogo.sizeMultiplier || 1.0}
                        onChange={(e) => setPanelConfig(prev => ({
                          ...prev,
                          customLogo: {
                            ...prev.customLogo,
                            sizeMultiplier: (() => {
                              const requested = parseFloat(e.target.value);
                              const next = Number.isFinite(requested) ? requested : 1.0;
                              const baseW = prev.customLogo?.width || 6;
                              const baseH = prev.customLogo?.height || 3;
                              const maxByWidth = Math.max(0.1, gridCols / Math.max(1, baseW));
                              const maxByHeight = Math.max(0.1, gridRows / Math.max(1, baseH));
                              const hardMax = Math.min(4.0, maxByWidth, maxByHeight);
                              return Math.max(0.5, Math.min(hardMax, next));
                            })()
                          }
                        }))}
                        inputProps={{ min: 0.5, max: 4.0, step: 0.1 }}
                        sx={{ width: 120 }}
                      />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        x
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Custom Text (engraved only — not connector reference labels) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Custom Text
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Engraved text — $0.20 per character. Connector reference labels (#n | type) are separate; use{' '}
                <strong>Show connector labels</strong> above the grid.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<TextIcon />}
                onClick={() => setShowTextDialog(true)}
              >
                Add Text
              </Button>
              {engravedCustomTextItems.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {engravedCustomTextItems.map((text) => (
                    <Chip
                      key={text.id}
                      label={text.content}
                      onDelete={() => setPanelConfig(prev => ({
                        ...prev,
                        customText: prev.customText.filter(t => t.id !== text.id)
                      }))}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Wire Tailings */}
            {panelConfig.connectors.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Wire Tailings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure wire specifications and connector selection for each panel connector
                    </Typography>
                  </Box>
                </Box>
                {panelConfig.connectors.map((connector) => {
                  const tailing = panelConfig.wireTailings.find(t => t.connectorId === connector.id);
                  return (
                    <Card key={connector.id} sx={{ mb: 3, p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {connector.type} at ({connector.position.row}, {connector.position.col})
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={tailing?.noTail ? 'none' : tailing ? 'configured' : null}
                          onChange={(_, v) => {
                            if (!v) return;
                            if (v === 'none') setNoWireTail(connector.id);
                            if (v === 'configured') initializeWireTailing(connector.id);
                          }}
                          sx={{
                            '& .MuiToggleButton-root': { textTransform: 'none', borderRadius: 1.5 },
                            '& .MuiToggleButton-root.Mui-selected': {
                              backgroundColor: 'rgba(179, 43, 43, 0.08)',
                              borderColor: '#b32b2b'
                            }
                          }}
                        >
                          <ToggleButton value="configured">Configure wire tailing</ToggleButton>
                          <ToggleButton value="none">No wire tail</ToggleButton>
                        </ToggleButtonGroup>

                        {tailing?.noTail ? (
                          <Chip
                            size="small"
                            label="No wire tail selected"
                            sx={{ backgroundColor: 'rgba(0,0,0,0.06)' }}
                          />
                        ) : null}
                      </Box>
                      
                      {tailing && !tailing.noTail && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Wire Tailing Configuration
                          </Typography>
                          
                          {/* End A (Panel Mount) - Fixed */}
                          <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              End A (Panel Mount) - Fixed
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {connector.image ? (
                                <img 
                                  src={connector.image} 
                                  alt={connector.type}
                                  style={{ width: 32, height: 32, objectFit: 'contain' }}
                                />
                              ) : (
                                <Box sx={{ width: 32, height: 32, backgroundColor: '#ddd', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {connector.type.substring(0, 3).toUpperCase()}
                                </Box>
                              )}
                              <Typography variant="body2" fontWeight="bold">
                                {connector.type} (Panel Mount)
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* End B (Cable Mount) - Selectable */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              End B (Cable Mount) - Select Connector
                            </Typography>
                            <Select
                              value={tailing.connectors?.endB?.connector || ''}
                              onChange={(e) => {
                                const nextEndB = e.target.value;
                                const endBConn = compatibilityMatrix[nextEndB];
                                const isFivePin = (conn) =>
                                  Boolean(conn && (conn.poles === 5 || (typeof conn.id === 'string' && conn.id.startsWith('NC5'))));

                                const shouldDefaultToDmx =
                                  isFivePin(endBConn) ||
                                  isFivePin(compatibilityMatrix[connector.connectorId]);

                                const nextWireType =
                                  // Only auto-switch to PSDMX when DMX is relevant and user is still on the PSM default (or unset).
                                  shouldDefaultToDmx && (!tailing.wireType || tailing.wireType === 'PSM')
                                    ? 'PSDMX'
                                    : tailing.wireType;

                                handleWireTailingChange(connector.id, {
                                  ...tailing,
                                  wireType: nextWireType,
                                  connectors: {
                                    ...tailing.connectors,
                                    endB: {
                                      connector: nextEndB,
                                      configuration: 'cable-mount'
                                    }
                                  }
                                });
                              }}
                              displayEmpty
                              fullWidth
                              MenuProps={{ disablePortal: true }}
                              sx={{ mb: 1, maxWidth: '50%' }}
                              startAdornment={
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                  {tailing.connectors?.endB?.connector ? (
                                    <Box sx={{ color: '#4caf50', fontSize: '16px' }}>✓</Box>
                                  ) : (
                                    <Box sx={{ color: '#B32B2B', fontSize: '16px' }}>✗</Box>
                                  )}
                                </Box>
                              }
                            >
                              <MenuItem value="">
                                <em>Select cable connector...</em>
                              </MenuItem>
                              {(() => {
                                // Get compatible connectors for the panel mount connector
                                const panelConnectorId = connector.connectorId;
                                const compatibleConnectors = Object.values(compatibilityMatrix).filter(conn => 
                                  conn.mountType === 'cable' && 
                                  conn.compatibleWith && 
                                  conn.compatibleWith.some(compat => compat.id === panelConnectorId)
                                );
                                
                                return compatibleConnectors.map(conn => (
                                  <MenuItem key={conn.id} value={conn.id}>
                                    <Tooltip
                                      title={
                                        <Box sx={{ textAlign: 'center' }}>
                                          {conn.image && (
                                            <img 
                                              src={conn.image} 
                                              alt={conn.name}
                                              style={{ 
                                                width: 80, 
                                                height: 80, 
                                                objectFit: 'contain',
                                                marginBottom: 8
                                              }}
                                            />
                                          )}
                                          <Typography variant="body2" fontWeight="bold">
                                            {conn.name}
                                          </Typography>
                                        </Box>
                                      }
                                      placement="right"
                                      arrow
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        {conn.image && (
                                          <img 
                                            src={conn.image} 
                                            alt={conn.name}
                                            style={{ width: 24, height: 24, objectFit: 'contain' }}
                                          />
                                        )}
                                        <Box>
                                          <Typography variant="body2" fontWeight="bold">
                                            {conn.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {conn.description || `${conn.type} ${conn.family} - ${conn.skuCode}`}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Tooltip>
                                  </MenuItem>
                                ));
                              })()}
                            </Select>
                          </Box>
                          
                          {/* Wire Type Selection */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Wire Type *
                            </Typography>
                            <Select
                              value={tailing.wireType || ''}
                              onChange={(e) => handleWireTailingChange(connector.id, {
                                ...tailing,
                                wireType: e.target.value
                              })}
                              fullWidth
                              MenuProps={{ disablePortal: true }}
                              sx={{ mb: 1, maxWidth: '50%' }}
                              displayEmpty
                              startAdornment={
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                  {(() => {
                                    // Get compatible wire types for the panel mount connector
                                    const panelConnectorId = connector.connectorId;
                                    const panelConnector = Object.values(compatibilityMatrix).find(conn => conn.id === panelConnectorId);
                                    const compatibleWireTypes = panelConnector?.wireCompatibility || [];
                                    
                                    // Only show green check if there's exactly one wire type and it's selected
                                    if (compatibleWireTypes.length === 1 && tailing.wireType === compatibleWireTypes[0]) {
                                      return <Box sx={{ color: '#4caf50', fontSize: '16px' }}>✓</Box>;
                                    } else if (tailing.wireType) {
                                      return <Box sx={{ color: '#4caf50', fontSize: '16px' }}>✓</Box>;
                                    } else {
                                      return <Box sx={{ color: '#B32B2B', fontSize: '16px' }}>✗</Box>;
                                    }
                                  })()}
                                </Box>
                              }
                              renderValue={(selected) => {
                                if (!selected) {
                                  return <Box sx={{ color: 'text.secondary' }}><em>Select wire type...</em></Box>;
                                }
                                
                                // Wire type verbose descriptions - match cable builder system
                                const wireTypeDescriptions = {
                                  'PSM': 'Professional Shielded Multi-Conductor Cable',
                                  'PSDMX': 'DMX 110Ω Cable',
                                  'PSP12': 'Professional Shielded Power Cable 12 AWG',
                                  'PSP14': 'Professional Shielded Power Cable 14 AWG', 
                                  'PSP16': 'Professional Shielded Power Cable 16 AWG',
                                  'PSP18': 'Professional Shielded Power Cable 18 AWG',
                                  'PSA14': 'Professional Shielded Audio Cable 14 AWG',
                                  'PSA16': 'Professional Shielded Audio Cable 16 AWG',
                                  'PSA18': 'Professional Shielded Audio Cable 18 AWG',
                                  'PSA20': 'Professional Shielded Audio Cable 20 AWG',
                                  'PSA22': 'Professional Shielded Audio Cable 22 AWG',
                                  'PSA24': 'Professional Shielded Audio Cable 24 AWG',
                                  'PSI': 'Professional Premium Cable',
                                  'PSS12': 'Professional Shielded Speaker Cable 12 AWG',
                                  'PSS14': 'Professional Shielded Speaker Cable 14 AWG',
                                  'PSS16': 'Professional Shielded Speaker Cable 16 AWG',
                                  'PSS18': 'Professional Shielded Speaker Cable 18 AWG',
                                  'PSS': 'Professional Shielded Speaker Cable',
                                  'Zip': 'Zip Cable',
                                  'Speaker': 'Speaker Cable'
                                };
                                
                                const description = wireTypeDescriptions[selected] || `${selected} Wire Type`;
                                return `${selected} - ${description}`;
                              }}
                            >
                              {(() => {
                                // Get compatible wire types for the panel mount connector
                                const panelConnectorId = connector.connectorId;
                                const panelConnector = Object.values(compatibilityMatrix).find(conn => conn.id === panelConnectorId);
                                const compatibleWireTypes = panelConnector?.wireCompatibility || [];
                                
                                // Wire type verbose descriptions - match cable builder system
                                const wireTypeDescriptions = {
                                  'PSM': 'Professional Shielded Multi-Conductor Cable',
                                  'PSDMX': 'DMX 110Ω Cable',
                                  'PSP12': 'Professional Shielded Power Cable 12 AWG',
                                  'PSP14': 'Professional Shielded Power Cable 14 AWG', 
                                  'PSP16': 'Professional Shielded Power Cable 16 AWG',
                                  'PSP18': 'Professional Shielded Power Cable 18 AWG',
                                  'PSA14': 'Professional Shielded Audio Cable 14 AWG',
                                  'PSA16': 'Professional Shielded Audio Cable 16 AWG',
                                  'PSA18': 'Professional Shielded Audio Cable 18 AWG',
                                  'PSA20': 'Professional Shielded Audio Cable 20 AWG',
                                  'PSA22': 'Professional Shielded Audio Cable 22 AWG',
                                  'PSA24': 'Professional Shielded Audio Cable 24 AWG',
                                  'PSI': 'Professional Premium Cable',
                                  'Zip': 'Zip Cable',
                                  'Speaker': 'Speaker Cable'
                                };
                                
                                return compatibleWireTypes.map(wireType => (
                                  <MenuItem key={wireType} value={wireType}>
                                    <Box>
                                      <Typography variant="body2" fontWeight="bold">
                                        {wireType}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {wireTypeDescriptions[wireType] || `${wireType} Wire Type`}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ));
                              })()}
                            </Select>
                          </Box>
                          
                          {/* Wire Length */}
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <TextField
                                label={`Wire Length (${wireLengthUnit === 'ft' ? 'feet' : 'inches'})`}
                                type="number"
                                value={(() => {
                                  const length = tailing.length || 72; // Default to 6 feet (72 inches)
                                  if (wireLengthUnit === 'ft') {
                                    // Convert inches to feet for display, round to 2 decimal places
                                    const feet = length / 12;
                                    return feet.toFixed(2).replace(/\.00$/, '');
                                  } else {
                                    // Display inches directly
                                    return length;
                                  }
                                })()}
                                onChange={(e) => {
                                  const inputValue = parseFloat(e.target.value) || 0;
                                  let newLength;
                                  if (wireLengthUnit === 'ft') {
                                    // Convert feet to inches for storage
                                    newLength = Math.round(inputValue * 12);
                                  } else {
                                    // Store inches directly
                                    newLength = Math.round(inputValue);
                                  }
                                  handleWireTailingChange(connector.id, {
                                    ...tailing,
                                    length: newLength
                                  });
                                }}
                                inputProps={{ 
                                  min: wireLengthUnit === 'ft' ? 0.1 : 1, 
                                  max: wireLengthUnit === 'ft' ? 500 : 6000, 
                                  step: wireLengthUnit === 'ft' ? 0.01 : 1 
                                }}
                                sx={{ width: 150 }}
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Units:
                                </Typography>
                                <ToggleButtonGroup
                                  value={wireLengthUnit}
                                  exclusive
                                  onChange={(e, newUnit) => {
                                    if (newUnit) {
                                      setWireLengthUnit(newUnit);
                                      // No need to convert existing lengths - they're stored in inches internally
                                      // The display conversion happens in the value calculation
                                    }
                                  }}
                                  aria-label="wire length unit selection"
                                  size="small"
                                >
                                  <ToggleButton value="in" aria-label="inches">In</ToggleButton>
                                  <ToggleButton value="ft" aria-label="feet">Ft</ToggleButton>
                                </ToggleButtonGroup>
                              </Box>
                            </Box>
                          </Box>
                          
                          {/* Special Instructions */}
                          <Box sx={{ mb: 2 }}>
                            <TextField
                              label="Special Instructions (Optional)"
                              multiline
                              rows={2}
                              value={tailing.specialInstructions || ''}
                              onChange={(e) => handleWireTailingChange(connector.id, {
                                ...tailing,
                                specialInstructions: e.target.value
                              })}
                              fullWidth
                            />
                          </Box>

                          {/* Finishing Options (per tailing) */}
                          <Accordion sx={{ mb: 2 }} defaultExpanded={false}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="subtitle2">Finishing (optional)</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Heat shrink (incl. custom logo/text) and XLR coding rings.
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                <strong>Note:</strong> Custom text/logo options apply to <strong>all</strong> custom tails.
                              </Typography>
                              <SimplifiedFinishingSelection
                                value={{
                                  ...(tailing.finishing || {}),
                                  // Always show the shared custom HS config if present so edits stay in sync.
                                  ...(panelConfig.tailSharedCustomHeatShrink
                                    ? { customHeatShrink: panelConfig.tailSharedCustomHeatShrink }
                                    : {})
                                }}
                                onChange={(finishing) => handleTailingFinishingChange(connector.id, tailing, finishing)}
                                sku={panelConfig.sku || designSku}
                                config={{
                                  connectors: {
                                    endA: connector.connectorId ? { id: connector.connectorId } : undefined,
                                    endB: tailing.connectors?.endB?.connector
                                      ? { id: tailing.connectors.endB.connector }
                                      : undefined
                                  }
                                }}
                              />
                            </AccordionDetails>
                          </Accordion>
                          
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => setPanelConfig(prev => ({
                              ...prev,
                              wireTailings: prev.wireTailings.filter(t => t.connectorId !== connector.id)
                            }))}
                          >
                            REMOVE WIRE TAILING
                          </Button>
                        </Box>
                      )}
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      )}


      {/* Step 3: Review and Price */}
      {activeStep === 2 && (
        <PanelReviewCart
          panelConfig={panelConfig}
          onAddToCart={handleAddToCart}
          addingToCart={bigCommerceLoading}
          proofImageUrl={proofImageUrl}
        />
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          disabled={activeStep === 0}
          onClick={() => setActiveStep(prev => prev - 1)}
          sx={{
            borderColor: '#b32b2b',
            color: '#b32b2b',
            '&:hover': {
              borderColor: '#8b2222',
              backgroundColor: 'rgba(179, 43, 43, 0.04)'
            },
            '&:disabled': {
              borderColor: '#e0e0e0',
              color: '#9e9e9e'
            }
          }}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          {activeStep === 1 && !wireTailingValidation.isValid && (
            <Box sx={{ maxWidth: 520, textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, mb: 0.25 }}>
                To continue, complete:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                {wireTailingValidation.missingRequirements.join(' • ')}
              </Typography>
            </Box>
          )}

          <Tooltip
            title={
              !wireTailingValidation.isValid && activeStep === 1 ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Complete tailing configuration:
                  </Typography>
                  {wireTailingValidation.missingRequirements.map((req, index) => (
                    <Typography key={index} variant="body2" sx={{ display: 'block', mb: 0.5 }}>
                      • {req}
                    </Typography>
                  ))}
                </Box>
              ) : ''
            }
            placement="top"
            arrow
          >
            <span>
              <Button
                variant="contained"
                disabled={activeStep === steps.length - 1 || (activeStep === 1 && !wireTailingValidation.isValid)}
                onClick={async () => {
            // If moving from step 1 (Connectors & Customization) to step 2 (Review & Price)
            // trigger image capture with popup overlay
            if (activeStep === 1) {
              try {
                console.log('📸 Capturing panel image before review...');
                
                // Show popup overlay
                setImageGenerationPopup(true);
                setImageGenerationProgress(0);
                
                // Start progress animation
                const startTime = Date.now();
                const minimumDelay = 8000; // 8 seconds minimum for panel generation
                
                progressIntervalRef.current = setInterval(() => {
                  setImageGenerationProgress(prev => {
                    // More realistic progress for 8-second process
                    // Target 90% over 8 seconds = ~11% per second
                    const newProgress = Math.min(prev + Math.random() * 15 + 5, 90);
                    return newProgress;
                  });
                }, 200); // Faster interval for smoother animation
                
                const proofUrl = await generatePanelImage();
                if (proofUrl) setProofImageUrl(proofUrl);
                console.log('✅ Panel image captured successfully');
                
                // Complete the loading progress
                setImageGenerationProgress(100);
                
                // Calculate elapsed time and ensure minimum delay
                const elapsedTime = Date.now() - startTime;
                const remainingDelay = Math.max(0, minimumDelay - elapsedTime);
                
                // Wait for remaining time before hiding overlay
                setTimeout(() => {
                  // Clear progress interval
                  if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current);
                    progressIntervalRef.current = null;
                  }
                  
                  // Hide overlay
                  setImageGenerationPopup(false);
                  setImageGenerationProgress(0);
                  
                  // Move to next step
                  setActiveStep(prev => prev + 1);
                }, remainingDelay + 500); // Add 500ms for 100% display
                
              } catch (error) {
                console.error('❌ Panel image capture failed:', error);
                
                // Clear progress interval on error
                if (progressIntervalRef.current) {
                  clearInterval(progressIntervalRef.current);
                  progressIntervalRef.current = null;
                }
                
                // Hide overlay and continue to next step
                setImageGenerationPopup(false);
                setImageGenerationProgress(0);
                setActiveStep(prev => prev + 1);
              }
            } else {
              setActiveStep(prev => prev + 1);
            }
          }}
          sx={{
            backgroundColor: '#b32b2b',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#8b2222'
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#9e9e9e'
            }
          }}
        >
          {activeStep === steps.length - 1 ? 'Complete' : 'Next'}
        </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Custom Text Dialog */}
      <Dialog open={showTextDialog} onClose={() => setShowTextDialog(false)}>
        <Box sx={{ p: 3, minWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Add Custom Text
          </Typography>
        <TextField
          label="Text Content"
          fullWidth
          value={customTextInput}
          onChange={(e) => setCustomTextInput(e.target.value)}
          helperText={
            <Box>
              <Typography
                variant="caption"
                color={customTextInput.length > textFormatting.maxCharacters ? 'error' :
                       customTextInput.length > textFormatting.maxCharacters * 0.8 ? 'warning.main' : 'text.secondary'}
              >
                {textFormatting.maxCharacters - customTextInput.length} characters remaining
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Text is engraved using a standard font.
              </Typography>
            </Box>
          }
          sx={{ mb: 2 }}
        />
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={() => setShowTextDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleAddCustomText}>
              Add Text
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Hidden file input for logo upload */}
      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        id="logo-upload"
        onChange={handleLogoUpload}
      />

      {/* BigCommerce Success/Error Notifications */}
      <Snackbar
        open={!!bigCommerceMessage}
        autoHideDuration={6000}
        onClose={() => {
          setBigCommerceMessage('');
          setCartUrl('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => {
            setBigCommerceMessage('');
            setCartUrl('');
          }}
          severity={bigCommerceSuccess ? 'success' : 'error'}
          sx={{ width: '100%' }}
          action={bigCommerceSuccess && cartUrl ? (
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => {
                window.open(cartUrl, '_blank');
              }}
              variant="outlined"
              sx={{ 
                color: 'inherit', 
                borderColor: 'currentColor',
                '&:hover': { 
                  borderColor: 'currentColor',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Add to Cart
            </Button>
          ) : null}
        >
          {bigCommerceMessage}
        </Alert>
      </Snackbar>
      
      {/* Image Generation Popup Overlay */}
      {imageGenerationPopup && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Paper
            elevation={24}
            sx={{
              padding: 4,
              textAlign: 'center',
              maxWidth: 400,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              borderRadius: 16,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                marginBottom: 3,
              }}
            >
              <CircularProgress
                size={80}
                thickness={4}
                variant="determinate"
                value={imageGenerationProgress}
                sx={{
                  color: 'error.main',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
              <Typography
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'error.main',
                }}
              >
                {Math.round(imageGenerationProgress)}%
              </Typography>
            </Box>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'error.main',
                mb: 2,
                background: 'linear-gradient(45deg, #B32B2B, #B32B2B)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Generating Your Custom Panel
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 3,
                lineHeight: 1.6,
                fontWeight: 500,
              }}
            >
              {imageGenerationProgress < 20 ? 'Analyzing your panel design...' :
               imageGenerationProgress < 40 ? 'Preparing connector images...' :
               imageGenerationProgress < 60 ? 'Generating panel layout...' :
               imageGenerationProgress < 80 ? 'Optimizing for production...' :
               'Finalizing your custom panel...'}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
              {[0, 1, 2, 3, 4].map((index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: index <= Math.floor(imageGenerationProgress / 20) ? 'error.main' : 'grey.300',
                    transition: 'all 0.3s ease',
                    transform: index === Math.floor(imageGenerationProgress / 20) ? 'scale(1.2)' : 'scale(1)',
                  }}
                />
              ))}
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                fontStyle: 'italic',
                opacity: 0.8,
              }}
            >
              This usually takes less than a minute...
            </Typography>
          </Paper>
        </Box>
      )}
      </Container>
    </Box>
  );
}

export default PanelBuilder;
